import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import useTheme from './useTheme';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

const _ATTENDANCE_DEDUP_WINDOW_MS = 60 * 1000;
const _attendanceSubmitLastSent = new Map();
function _attendanceSubmitCanGo(userId, action, nowMs = Date.now()) {
  if (!userId || !action) return { ok: true, remainingMs: 0 };
  const key = `${String(userId)}|${String(action).toLowerCase()}`;
  const lastSent = _attendanceSubmitLastSent.get(key) || 0;
  const elapsed = nowMs - lastSent;
  if (lastSent && elapsed < _ATTENDANCE_DEDUP_WINDOW_MS) {
    return { ok: false, remainingMs: _ATTENDANCE_DEDUP_WINDOW_MS - elapsed };
  }
  return { ok: true, remainingMs: 0 };
}
function _attendanceSubmitMarkSent(userId, action, nowMs = Date.now()) {
  if (!userId || !action) return;
  const key = `${String(userId)}|${String(action).toLowerCase()}`;
  _attendanceSubmitLastSent.set(key, nowMs);
}
function _attendanceSubmitClearMark(userId, action) {
  if (!userId || !action) return;
  const key = `${String(userId)}|${String(action).toLowerCase()}`;
  _attendanceSubmitLastSent.delete(key);
}

const pad = (n) => n.toString().padStart(2, '0');

const EN_MONTHS_DOT = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];
const formatDateAsRequested = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const m = EN_MONTHS_DOT[d.getMonth()];
  const day = pad(d.getDate());
  const y = d.getFullYear();
  return `${m} ${day}, ${y}`;
};
const formatTime12AmPm = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  let h = d.getHours();
  const m = pad(d.getMinutes());
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${pad(h)}:${m} ${suffix}`;
};

// ============================================================================
// Geolocation helpers (revised 2026-09-30 — desktop GPS delay fix)
//
// Pipeline overview:
//   1. `geoCache` — module-level singleton warm cache (TTL 5 minutes). When the
//      employee lands on the time-tracking page, `prefetchFastGeo()` is called
//      once at mount. By the time the user clicks "Time In", the cache already
//      holds a resolved location → modal open shows the pin instantly.
//   2. `acquireFastGeo()` — race:
//        • Primary: `navigator.geolocation` with DESKTOP-optimized params
//          (enableHighAccuracy=false on desktop, 10s timeout, 5min maxAge).
//        • 5s progressive fallback — after 5s without onReady, fire the
//          backend `/api/ip-geolocation` route in parallel. Whichever succeeds
//          first (with reasonable lat/lng shape) wins. If the primary GPS later
//          settles with a better accuracy, onUpdate fires but the confirm
//          button still remains enabled from the backup.
//   3. `onStatus` — 4-step textual state machine that's displayed inside the
//      modal so users know what's happening instead of a single static spinner:
//          a. "Getting your location from your device…" (0–5s)
//          b. "Device location taking long — trying IP-based backup…" (5s mark)
//          c. "Location ready — please verify pin on the map" (ready)
//          d. Friendly errors with mitigation steps, not the old "Step outdoors"
//             text that's nonsensical on a desktop PC in an office.
// ============================================================================

const GEO_CACHE_TTL_MS = 5 * 60 * 1000;
const GEO_PRIMARY_TIMEOUT_MS = 10000;
const GEO_FALLBACK_TRIGGER_MS = 5000;
const GEO_OVERALL_TIMEOUT_MS = 10000;
const GEO_CACHE_KEY = '__isource_geo_warm_cache_v1__';
const GEO_PREFETCH_ONCE_FLAG_KEY = '__isource_geo_prefetch_started_v1__';
const GEO_DESKTOP_ACCURACY_THRESHOLD_M = 150;
const GEO_IP_ACCURACY_FALLBACK_M = 3000;

function _getGeoCache() {
  try {
    const raw = window[GEO_CACHE_KEY];
    if (!raw) return null;
    const age = Date.now() - (raw.ts || 0);
    if (age >= GEO_CACHE_TTL_MS) { try { delete window[GEO_CACHE_KEY]; } catch (_) {} return null; }
    return raw.value;
  } catch (_) { return null; }
}
function _setGeoCache(value) {
  try { window[GEO_CACHE_KEY] = { ts: Date.now(), value }; } catch (_) {}
}
function _clearGeoCache() {
  try { delete window[GEO_CACHE_KEY]; } catch (_) {}
}

function _isLikelyDesktop() {
  try {
    const ua = (navigator.userAgent || '').toLowerCase();
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile\s|mobi\b|phone/i.test(ua);
    if (isMobile) return false;
    if (navigator.maxTouchPoints && navigator.maxTouchPoints > 1) return false;
    return true;
  } catch (_) { return true; }
}

const reverseGeocode = async (lat, lng) => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=en&format=json`;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 3500) : null;
    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined,
    });
    if (timeoutId) clearTimeout(timeoutId);
    if (!res.ok) return { address: '', city: '' };
    const data = await res.json();
    const fullAddress = typeof data?.display_name === 'string' ? data.display_name : '';
    const a = data?.address || {};
    const city =
      a.city || a.town || a.municipality || a.village ||
      a.county || a.state_district || a.state || a.country || '';
    return { address: fullAddress, city };
  } catch (_) {
    return { address: '', city: '' };
  }
};

const _fetchIpFallback = async (axiosInstanceRef) => {
  try {
    const inst = (axiosInstanceRef && typeof axiosInstanceRef.get === 'function')
      ? axiosInstanceRef
      : (typeof axios !== 'undefined' ? axios : null);
    if (!inst) return null;
    const res = await inst.get('/ip-geolocation', { timeout: 4500 });
    const d = (res && (res.data || res)) || null;
    if (!d || !d.ok) return null;
    const lat = Number(d.lat), lng = Number(d.lng);
    if (!(Number.isFinite(lat) && Number.isFinite(lng))) return null;
    return {
      lat,
      lng,
      accuracy: Number.isFinite(Number(d.accuracy)) ? Math.max(Number(d.accuracy), GEO_IP_ACCURACY_FALLBACK_M) : GEO_IP_ACCURACY_FALLBACK_M,
      address: typeof d.address === 'string' ? d.address : '',
      city_name: typeof d.city === 'string' ? d.city : (typeof d.region === 'string' ? d.region : ''),
      source: d.source === 'ip_geolocation' ? 'ip_fallback' : 'ip_fallback',
      note: typeof d.note === 'string' ? d.note : '',
    };
  } catch (_) {
    return null;
  }
};

/**
 * Non-interactive page-level warm prefetch (called when Employee Landing Page
 * mounts, regardless of modal being open). Never touches modal state or
 * triggers user-visible errors; just writes to the module-level cache.
 */
export function prefetchFastGeo({ axiosInstance } = {}) {
  try {
    if (typeof window === 'undefined') return () => {};
    if (_getGeoCache()) return () => {};
    // SINGLETON GUARD: If EmployeeLandingPage's parent re-renders before the
    // cache is populated (e.g., 1 Hz clock tick in AnalogClock → component
    // re-renders → new axiosInstance reference → useEffect re-runs), this
    // flag prevents the thundering herd of N acquireFastGeo calls, N
    // watchPosition watchers, and N 5-second fallback timers that would each
    // fire /api/ip-geolocation in parallel 5 seconds later and starve the
    // backend uvicorn worker pool (causing every other API call to queue for
    // 30 seconds and timeout with Axios ECONNABORTED).
    try {
      if (window[GEO_PREFETCH_ONCE_FLAG_KEY]) return () => {};
      window[GEO_PREFETCH_ONCE_FLAG_KEY] = true;
    } catch (_) {}
    const noop = () => {};
    let stopFn = () => {};
    try {
      stopFn = acquireFastGeo({
        axiosInstance,
        onReady: (payload) => {
          if (!payload) return;
          _setGeoCache({ ...payload, readyAt: Date.now() });
        },
        onError: noop,
        onTimeout: noop,
        onUpdate: noop,
        onStatus: noop,
      }).stop;
    } catch (_) {}
    let cancelled = false;
    const auto = setTimeout(() => { if (!cancelled) try { stopFn && stopFn(); } catch (_) {} }, GEO_OVERALL_TIMEOUT_MS + 1000);
    return () => {
      cancelled = true;
      try { clearTimeout(auto); } catch (_) {}
      try { stopFn && stopFn(); } catch (_) {}
    };
  } catch (_) {
    return () => {};
  }
}

const acquireFastGeo = ({ axiosInstance, onUpdate, onReady, onError, onTimeout, onStatus, _forceDesktop }) => {
  if (typeof navigator === 'undefined') {
    onError?.({ code: 'NO_GPS', message: 'Device location not available in this environment.' });
    return { stop: () => {} };
  }
  if (!navigator.geolocation) {
    onError?.({ code: 'NO_GPS', message: 'This browser does not support device location. Please use Chrome, Edge, or Safari with HTTPS.' });
    return { stop: () => {} };
  }
  if (!window.isSecureContext) {
    onError?.({ code: 'INSECURE', message: 'Location requires a secure HTTPS connection. Please reload over https:// to enable location.' });
    return { stop: () => {} };
  }

  const isDesktop = typeof _forceDesktop === 'boolean' ? _forceDesktop : _isLikelyDesktop();
  // Desktop optimized params.
  const enableHighAccuracy = isDesktop ? false : true;
  const maximumAge = GEO_CACHE_TTL_MS;
  const timeoutMs = GEO_PRIMARY_TIMEOUT_MS;

  let watchId = null;
  let fallbackTimerId = null;
  let overallTimerId = null;
  let statusTimerId = null;
  let ipFallbackStarted = false;
  let stopped = false;
  let resolvedReady = false;
  let best = {
    accuracy: Infinity, lat: null, lng: null, samples: 0, source: null,
    address: null, city_name: null, note: null,
  };

  // ---- status helpers ------------------------------------------------------
  const setStatus = (phase, extra) => {
    if (stopped) return;
    let text = '';
    switch (phase) {
      case 'device': text = isDesktop
        ? 'Getting your location from your device (desktop optimized)…'
        : 'Getting your actual location from device GPS…'; break;
      case 'ip_trying': text = 'Device location is taking longer than usual — trying a fast IP-based backup…'; break;
      case 'cache_hit': text = 'Using pre-fetched location from cache.'; break;
      case 'ready': text = 'Location ready — please verify the pin on the map.'; break;
      case 'permission_prompt': text = 'Waiting for browser location permission…'; break;
      default: text = '';
    }
    onStatus?.({ phase, text, isDesktop, ...(extra || {}) });
  };

  const tryFinalize = (override) => {
    if (stopped || resolvedReady) return;
    const payload = override || (best.lat != null && best.lng != null
      ? { lat: best.lat, lng: best.lng,
          accuracy: best.accuracy === Infinity ? null : Math.round(best.accuracy),
          address: best.address || '', city_name: best.city_name || '',
          source: best.source || 'gps',
          note: best.note || '' }
      : null);
    if (!payload) return;
    // Need address/city to fill the "location" field displayed above the map.
    const ensureAddress = (() => new Promise((resolve) => {
      if (typeof payload.address === 'string' && payload.address.trim()) {
        resolve({ address: payload.address, city: payload.city_name || '' });
        return;
      }
      reverseGeocode(payload.lat, payload.lng).then(resolve).catch(() => resolve({ address: '', city: '' }));
    }));
    ensureAddress().then(({ address, city }) => {
      if (stopped || resolvedReady) return;
      resolvedReady = true;
      // Try to update cache with the final payload so confirm-button press < 3s.
      try {
        _setGeoCache({
          lat: payload.lat, lng: payload.lng,
          accuracy: payload.accuracy != null ? payload.accuracy : null,
          address, city_name: city,
          source: payload.source, readyAt: Date.now(),
        });
      } catch (_) {}
      cleanupTimers();
      clearPrimaryWatch();
      const finalPayload = { ...payload, address: address || payload.address || `${payload.lat.toFixed(6)}, ${payload.lng.toFixed(6)}`,
        city_name: city || payload.city_name || '' };
      setStatus('ready', finalPayload);
      onReady?.(finalPayload);
    });
  };

  const applySample = (lat, lng, accuracy, source) => {
    if (stopped || resolvedReady) return;
    if (!(Number.isFinite(lat) && Number.isFinite(lng))) return;
    const acc = Number.isFinite(accuracy) && accuracy > 0 ? accuracy : 9999;
    best.samples += 1;
    // Only replace "best" if the new sample is clearly better (lower accuracy),
    // or source is strictly preferred (gps over ip_fallback).
    const curBetterThanExisting = (() => {
      if (best.accuracy === Infinity) return true;
      if (source === 'gps' && best.source === 'ip_fallback') return true;
      if (best.source === 'gps' && source === 'ip_fallback') return false;
      return acc < best.accuracy;
    })();
    if (curBetterThanExisting) {
      best.accuracy = acc; best.lat = lat; best.lng = lng; best.source = source;
    }
    onUpdate?.({
      sample: best.samples,
      bestAccuracy: best.accuracy === Infinity ? null : Math.round(best.accuracy),
      currentAccuracy: acc === 9999 ? null : Math.round(acc),
      source, isDesktop,
    });
    // Threshold for early-finalize (device) on desktop: 150m accuracy and ≥1 sample.
    // This matches workplace-branch level (±150m), tight enough for branch
    // verification, loose enough to succeed within 3s on Wi-Fi triangulation.
    const canFinalizePrimary =
      source === 'gps' &&
      best.lat != null && best.lng != null &&
      (best.accuracy <= GEO_DESKTOP_ACCURACY_THRESHOLD_M || best.samples >= 3);
    const canFinalizeFallback =
      source === 'ip_fallback' && best.lat != null && best.lng != null;
    if (canFinalizePrimary || canFinalizeFallback) {
      tryFinalize();
    }
  };

  const onPrimaryPosition = (pos) => {
    const c = pos?.coords;
    if (!c) return;
    applySample(c.latitude, c.longitude, c.accuracy, 'gps');
  };

  const onPrimaryError = (err) => {
    if (stopped || resolvedReady) return;
    const code = err?.code;
    if (code === 1) {
      onError?.({
        code: 'PERMISSION_DENIED',
        message: 'Location permission blocked. Allow this website to access your location in browser settings, then tap Update.',
      });
      return;
    }
    if (code === 2) {
      // Position unavailable → jump straight to IP fallback without waiting.
      if (!ipFallbackStarted) {
        ipFallbackStarted = true;
        setStatus('ip_trying');
        _fetchIpFallback(axiosInstance).then((ipRes) => {
          if (stopped || resolvedReady || !ipRes) return;
          applySample(ipRes.lat, ipRes.lng, ipRes.accuracy, 'ip_fallback');
          best.address = best.address || ipRes.address;
          best.city_name = best.city_name || ipRes.city_name;
          best.note = best.note || ipRes.note;
          tryFinalize();
        }).catch(() => {});
      }
      return;
    }
    if (code === 3) {
      // Primary timed out alone. If we haven't fired fallback yet, do it now.
      if (!ipFallbackStarted) {
        ipFallbackStarted = true;
        setStatus('ip_trying');
        _fetchIpFallback(axiosInstance).then((ipRes) => {
          if (stopped || resolvedReady || !ipRes) return;
          applySample(ipRes.lat, ipRes.lng, ipRes.accuracy, 'ip_fallback');
          best.address = best.address || ipRes.address;
          best.city_name = best.city_name || ipRes.city_name;
          best.note = best.note || ipRes.note;
          tryFinalize();
        }).catch(() => {});
      }
      return;
    }
    // Unknown error — swallow and let the IP fallback continue.
  };

  const clearPrimaryWatch = () => {
    if (watchId != null) {
      try { navigator.geolocation.clearWatch(watchId); } catch (_) {}
      watchId = null;
    }
  };

  const cleanupTimers = () => {
    [fallbackTimerId, overallTimerId, statusTimerId].forEach((id) => {
      if (id != null) try { clearTimeout(id); } catch (_) {}
    });
    fallbackTimerId = overallTimerId = statusTimerId = null;
  };

  // ---- 1. Cache hit (prefetch) ------------------------------------------------
  const cached = _getGeoCache();
  if (cached && Number.isFinite(cached.lat) && Number.isFinite(cached.lng)) {
    const age = Date.now() - (cached.readyAt || 0);
    if (age < GEO_CACHE_TTL_MS) {
      setStatus('cache_hit', cached);
      // Settle in microtask so caller can wire up stopFn synchronously.
      Promise.resolve().then(() => tryFinalize({ ...cached }));
    }
  }

  // ---- 2. Primary watchPosition ----------------------------------------------
  setStatus('device');
  try {
    // Single watchPosition — no additional getCurrentPosition (doubles work,
    // duplicates samples, and caused the earlier 28s hang on Ethernet PCs).
    watchId = navigator.geolocation.watchPosition(
      onPrimaryPosition,
      onPrimaryError,
      { enableHighAccuracy, maximumAge, timeout: timeoutMs },
    );
  } catch (err) {
    onError?.({ code: 'NO_GPS', message: (err && err.message) || 'Unable to start device location.' });
    return { stop: () => {} };
  }

  // ---- 3. 5-second progressive IP fallback trigger -------------------------
  fallbackTimerId = setTimeout(() => {
    if (stopped || resolvedReady) return;
    if (ipFallbackStarted) return;
    ipFallbackStarted = true;
    setStatus('ip_trying');
    _fetchIpFallback(axiosInstance).then((ipRes) => {
      if (stopped || resolvedReady || !ipRes) return;
      applySample(ipRes.lat, ipRes.lng, ipRes.accuracy, 'ip_fallback');
      best.address = best.address || ipRes.address;
      best.city_name = best.city_name || ipRes.city_name;
      best.note = best.note || ipRes.note;
      tryFinalize();
    }).catch(() => {});
  }, GEO_FALLBACK_TRIGGER_MS);

  // ---- 4. Overall timeout 10s (never "hang forever" -------------------------
  overallTimerId = setTimeout(() => {
    if (stopped || resolvedReady) return;
    // If we have any best-effort lat/lng (even rough IP fallback 3000m) —
    // accept it with a note. Otherwise surface the user-actionable timeout.
    if (best.lat != null && best.lng != null) {
      tryFinalize();
      return;
    }
    onTimeout?.({
      bestAccuracy: best.accuracy === Infinity ? null : Math.round(best.accuracy),
      samples: best.samples,
      isDesktop,
    });
  }, GEO_OVERALL_TIMEOUT_MS);

  const stop = () => {
    if (stopped) return;
    stopped = true;
    cleanupTimers();
    clearPrimaryWatch();
  };

  return { stop };
};

// Backward-compatible alias (used by runGpsCycle callback below until
// ConfirmLocationModal is fully migrated). Accepts the old signature by
// re-shaping into the new pipeline.
const acquireExactGps = ({ onUpdate, onReady, onError, onTimeout }) => acquireFastGeo({
  onUpdate, onReady, onError, onTimeout,
  onStatus: () => {},
});


function AnalogClock() {
  const { isLight } = useTheme();
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const seconds = time.getSeconds();
  const minutes = time.getMinutes();
  const hours = time.getHours() % 12;

  const secondDeg = seconds * 6;
  const minuteDeg = minutes * 6 + seconds * 0.1;
  const hourDeg = hours * 30 + minutes * 0.5;

  const hourMarkers = Array.from({ length: 12 }, (_, i) => i);
  const minuteMarkers = Array.from({ length: 60 }, (_, i) => i);

  const faceOuter = isLight ? '#ffffff' : '#0a1734';
  const faceInner = isLight ? '#f1f5f9' : '#071027';
  const ringOuter = isLight ? '#cbd5e1' : '#16306b';
  const ringInner = isLight ? '#e2e8f0' : '#0e2049';
  const minuteTick = isLight ? '#94a3b8' : '#3b5998';
  const hourTick = isLight ? '#475569' : '#60a5fa';
  const hourNum = isLight ? '#1e293b' : '#cbd5e1';
  const handHour = isLight ? '#0f172a' : '#e2e8f0';
  const handMinute = isLight ? '#2563eb' : '#93c5fd';
  const handSecond = isLight ? '#e11d48' : '#f87171';
  const centerDot = isLight ? '#b45309' : '#facc15';
  const centerCore = isLight ? '#fef3c7' : '#fef9c3';

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-56 h-56 md:w-64 md:h-64">
        <svg viewBox="0 0 200 200" className="w-full h-full drop-shadow-xl">
          <defs>
            <radialGradient id="clockFace" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={faceOuter} />
              <stop offset="100%" stopColor={faceInner} />
            </radialGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <circle cx="100" cy="100" r="97" fill="none" stroke={ringOuter} strokeWidth="2" />
          <circle cx="100" cy="100" r="95" fill="url(#clockFace)" />
          <circle cx="100" cy="100" r="93" fill="none" stroke={ringInner} strokeWidth="1" />

          {minuteMarkers.map((i) => {
            const isHour = i % 5 === 0;
            if (isHour) return null;
            const angle = (i * 6 - 90) * (Math.PI / 180);
            const x1 = 100 + 88 * Math.cos(angle);
            const y1 = 100 + 88 * Math.sin(angle);
            const x2 = 100 + 85 * Math.cos(angle);
            const y2 = 100 + 85 * Math.sin(angle);
            return (
              <line key={`min-${i}`} x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={minuteTick} strokeWidth="1" />
            );
          })}

          {hourMarkers.map((i) => {
            const angle = (i * 30 - 90) * (Math.PI / 180);
            const x1 = 100 + 88 * Math.cos(angle);
            const y1 = 100 + 88 * Math.sin(angle);
            const x2 = 100 + 78 * Math.cos(angle);
            const y2 = 100 + 78 * Math.sin(angle);
            const tx = 100 + 68 * Math.cos(angle);
            const ty = 100 + 68 * Math.sin(angle);
            const hourNumVal = i === 0 ? 12 : i;
            return (
              <g key={`hour-${i}`}>
                <line x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke={hourTick} strokeWidth="2.5" strokeLinecap="round" />
                <text x={tx} y={ty} textAnchor="middle" dominantBaseline="central"
                  fill={hourNum} fontSize="11" fontWeight="600" fontFamily="system-ui">
                  {hourNumVal}
                </text>
              </g>
            );
          })}

          <line
            x1="100" y1="100"
            x2="100" y2="48"
            stroke={handHour} strokeWidth="4" strokeLinecap="round"
            transform={`rotate(${hourDeg}, 100, 100)`}
          />

          <line
            x1="100" y1="100"
            x2="100" y2="30"
            stroke={handMinute} strokeWidth="2.5" strokeLinecap="round"
            transform={`rotate(${minuteDeg}, 100, 100)`}
          />

          <g transform={`rotate(${secondDeg}, 100, 100)`}>
            <line x1="100" y1="115" x2="100" y2="25"
              stroke={handSecond} strokeWidth="1.2" strokeLinecap="round"
              filter="url(#glow)" />
          </g>

          <circle cx="100" cy="100" r="4" fill={centerDot} />
          <circle cx="100" cy="100" r="2" fill={centerCore} />
        </svg>
      </div>
    </div>
  );
}

function DigitalClock() {
  const { isLight } = useTheme();
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;

  const dateStr = time.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const mainText = isLight ? 'text-white' : 'text-white';
  const accentText = isLight ? 'text-amber-600' : 'text-yellow-400';
  const secondText = isLight ? 'text-blue-600' : 'text-red-400';
  const ampmText = isLight ? 'text-blue-700' : 'text-blue-300';
  const subText = isLight ? 'text-slate-500' : 'text-slate-400';

  return (
    <div className="text-center">
      <div className="flex items-baseline justify-center gap-1">
        <span className={`text-4xl md:text-5xl font-mono font-bold tracking-wider ${mainText}`}>
          {pad(displayHours)}
        </span>
        <span className={`text-4xl md:text-5xl font-mono font-bold animate-pulse ${accentText}`}>
          :
        </span>
        <span className={`text-4xl md:text-5xl font-mono font-bold tracking-wider ${mainText}`}>
          {pad(minutes)}
        </span>
        <span className={`text-4xl md:text-5xl font-mono font-bold animate-pulse ${accentText}`}>
          :
        </span>
        <span className={`text-4xl md:text-5xl font-mono font-bold tracking-wider ${secondText}`}>
          {pad(seconds)}
        </span>
        <span className={`text-lg md:text-xl font-semibold ml-2 ${ampmText}`}>
          {ampm}
        </span>
      </div>
      <p className={`text-sm mt-2 tracking-wide ${subText}`}>{dateStr}</p>
    </div>
  );
}

function GreetingModal({ isOpen, type, employeeName, onContinue, onClose }) {
  const { isLight } = useTheme();
  const [input, setInput] = useState('');

  useEffect(() => {
    if (!isOpen) setInput('');
  }, [isOpen]);

  if (!isOpen) return null;

  const firstName = employeeName.split(' ')[0];

  const handleContinue = () => {
    onContinue(input.trim());
    setInput('');
  };

  const handleClose = () => {
    setInput('');
    onClose();
  };

  const modalBg = isLight ? 'bg-white' : 'bg-navy-900';
  const modalBorder = isLight ? 'border-slate-200' : 'border-navy-700/50';
  const headerBorder = isLight ? 'border-slate-200' : 'border-navy-700/50';
  const titleText = isLight ? 'text-white' : 'text-white';
  const closeBg = isLight ? 'bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-white' : 'bg-navy-850 text-slate-400 hover:text-white hover:bg-navy-800';
  const subText = isLight ? 'text-slate-500' : 'text-slate-400';
  const accentIn = isLight ? 'text-emerald-700' : 'text-emerald-400';
  const accentOut = isLight ? 'text-orange-600' : 'text-orange-400';
  const accentBgIn = isLight ? 'bg-emerald-50' : 'bg-emerald-500/20';
  const accentBgOut = isLight ? 'bg-orange-50' : 'bg-orange-500/20';
  const inputBg = isLight ? 'bg-slate-50 border-slate-200 text-white placeholder:text-slate-400 focus:border-blue-500/50 focus:ring-blue-500/30' : 'bg-navy-850/80 border-navy-700/50 text-white placeholder:text-slate-400 focus:border-blue-500/50 focus:ring-blue-500/30';
  const inputIcon = isLight ? 'text-slate-400' : 'text-slate-500';
  const cancelBtn = isLight ? 'bg-slate-100 text-white hover:bg-slate-200' : 'bg-navy-800 text-slate-200 hover:bg-navy-700';
  const continueIn = isLight ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-emerald-600 hover:bg-emerald-500';
  const continueOut = isLight ? 'bg-orange-600 hover:bg-orange-500' : 'bg-orange-600 hover:bg-orange-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className={`${modalBg} rounded-2xl overflow-hidden w-full max-w-md border ${modalBorder} shadow-2xl`}>
        <div className={`flex items-center justify-between p-4 border-b ${headerBorder}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${type === 'in' ? accentBgIn : accentBgOut}`}>
              {type === 'in' ? (
                <svg className={`w-5 h-5 ${type === 'in' ? accentIn : accentOut}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 11l5-5m0 0l5 5m-5-5v12" />
                </svg>
              ) : (
                <svg className={`w-5 h-5 ${type === 'in' ? accentIn : accentOut}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                </svg>
              )}
            </div>
            <h3 className={`font-semibold ${titleText}`}>
              {type === 'in' ? 'Time In' : 'Time Out'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${closeBg}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          <div className="text-center mb-4">
            <span className="text-5xl">{type === 'in' ? '👋' : '🌟'}</span>
          </div>

          <p className={`text-center text-lg font-medium leading-relaxed mb-6 ${titleText}`}>
            {type === 'in' ? (
              <>
                Hi <span className={`font-bold ${accentIn}`}>{firstName}</span>! Glad you arrived safely.
                <br />
                <span className={`text-sm mt-1 block ${subText}`}>
                  Which office/location have you arrived at?
                </span>
              </>
            ) : (
              <>
                Hey <span className={`font-bold ${accentOut}`}>{firstName}</span>! Time flew by!
                <br />
                <span className={`text-sm mt-1 block ${subText}`}>
                  Safe travels to your next stop!
                </span>
              </>
            )}
          </p>

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <svg className={`w-4 h-4 ${inputIcon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && input.trim()) handleContinue();
              }}
              placeholder={type === 'in' ? 'e.g. Manila Main Office' : 'e.g. Heading home'}
              className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-1 text-sm transition-all ${inputBg}`}
              autoFocus
            />
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={handleClose}
            className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors ${cancelBtn}`}
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={!input.trim()}
            className={`flex-1 px-4 py-2.5 rounded-xl text-white font-medium transition-colors flex items-center justify-center gap-2
              disabled:opacity-40 disabled:cursor-not-allowed ${
              type === 'in' ? continueIn : continueOut
            }`}
          >
            Continue
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function CameraModal({ isOpen, mode, onCapture, onClose }) {
  const { isLight } = useTheme();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [captured, setCaptured] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      stopStream();
      setCaptured(null);
      setError(null);
      setIsLoading(true);
      return;
    }

    const startCamera = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const facingMode = mode === 'selfie' ? 'user' : 'environment';

        let mediaStream;
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: false,
          });
        } catch {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
        }

        streamRef.current = mediaStream;
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          await videoRef.current.play();
        }
        setIsLoading(false);
      } catch (err) {
        console.error('Camera error:', err);
        setError('Unable to access camera. Please ensure camera permissions are granted.');
        setIsLoading(false);
      }
    };

    startCamera();

    return () => {
      stopStream();
    };
  }, [isOpen, mode, stopStream]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        if (mode === 'selfie') {
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
        }
        ctx.drawImage(video, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setCaptured(dataUrl);
      }
    }
  };

  const retake = () => {
    setCaptured(null);
  };

  const confirmPhoto = () => {
    if (captured) {
      stopStream();
      onCapture(captured);
    }
  };

  if (!isOpen) return null;

  const modalBg = isLight ? 'bg-white' : 'bg-navy-900';
  const modalBorder = isLight ? 'border-slate-200' : 'border-navy-700/50';
  const headerBorder = isLight ? 'border-slate-200' : 'border-navy-700/50';
  const titleText = isLight ? 'text-white' : 'text-white';
  const subText = isLight ? 'text-slate-500' : 'text-slate-400';
  const closeBg = isLight ? 'bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-white' : 'bg-navy-850 text-slate-400 hover:text-white hover:bg-navy-800';
  const loadingText = isLight ? 'text-slate-500' : 'text-slate-400';
  const accentSelfie = isLight ? 'bg-blue-50 text-blue-600' : 'bg-blue-500/20 text-blue-400';
  const accentPremises = isLight ? 'bg-orange-50 text-orange-600' : 'bg-orange-500/20 text-orange-400';
  const camBg = isLight ? 'bg-slate-100' : 'bg-black';
  const retakeBtn = isLight ? 'bg-slate-100 text-white hover:bg-slate-200' : 'bg-navy-800 text-slate-200 hover:bg-navy-700';
  const confirmBtn = 'bg-blue-600 text-white hover:bg-blue-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className={`${modalBg} rounded-2xl overflow-hidden w-full max-w-lg border ${modalBorder} shadow-2xl`}>
        <div className={`flex items-center justify-between p-4 border-b ${headerBorder}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${mode === 'selfie' ? accentSelfie : accentPremises}`}>
              {mode === 'selfie' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              )}
            </div>
            <div>
              <h3 className={`font-semibold ${titleText}`}>
                {mode === 'selfie' ? 'Take a Selfie' : 'Premises Photo'}
              </h3>
              <p className={`text-xs ${subText}`}>
                {mode === 'selfie'
                  ? 'Position your face in the frame'
                  : 'Capture your workplace premises'}
              </p>
            </div>
          </div>
          <button
            onClick={() => { stopStream(); onClose(); }}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${closeBg}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={`relative aspect-[4/3] ${camBg}`}>
          {isLoading && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10">
              <div className="w-10 h-10 border-3 border-blue-400 border-t-transparent rounded-full animate-spin" />
              <p className={`text-sm ${loadingText}`}>Initializing camera...</p>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center z-10">
              <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover ${mode === 'selfie' ? 'scale-x-[-1]' : ''} ${captured ? 'invisible' : ''}`}
          />

          {captured && (
            <img
              src={captured}
              alt="Captured"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}

          {mode === 'selfie' && !captured && !isLoading && !error && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-40 h-52 border-2 border-white/30 rounded-[50%]" />
            </div>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />

        <div className="p-4 flex items-center justify-center gap-4">
          {!captured ? (
            <button
              onClick={capturePhoto}
              disabled={isLoading || !!error}
              className="w-16 h-16 rounded-full bg-white/10 border-4 border-white flex items-center justify-center
                hover:bg-white/20 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="w-12 h-12 rounded-full bg-white" />
            </button>
          ) : (
            <>
              <button
                onClick={retake}
                className={`px-6 py-2.5 rounded-xl font-medium transition-colors ${retakeBtn}`}
              >
                Retake
              </button>
              <button
                onClick={confirmPhoto}
                className={`px-6 py-2.5 rounded-xl font-medium transition-colors flex items-center gap-2 ${confirmBtn}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Confirm
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const PH_CITY_COORDS_CONFIRM = {
  mandaluyong: [14.5794, 121.0359],
  manila: [14.5995, 120.9842],
  quezon: [14.6760, 121.0437],
  'quezon city': [14.6760, 121.0437],
  makati: [14.5547, 121.0245],
  bgc: [14.5516, 121.0503],
  taguig: [14.5245, 121.0755],
  pasig: [14.5764, 121.0851],
  cebu: [10.3157, 123.8854],
  'cebu city': [10.3157, 123.8854],
  davao: [7.1907, 125.4553],
  'davao city': [7.1907, 125.4553],
  'general santos': [6.1164, 125.1716],
  gensan: [6.1164, 125.1716],
  batangas: [13.7565, 121.0583],
  'batangas city': [13.7565, 121.0583],
  calamba: [14.2057, 121.1588],
  pampanga: [15.0336, 120.6839],
  bulacan: [14.7926, 120.8821],
  laguna: [14.3000, 121.3000],
  cavite: [14.2360, 120.8825],
  imus: [14.4283, 120.9363],
  paranaque: [14.5066, 121.0052],
  'las pinas': [14.4806, 120.9762],
  muntinlupa: [14.4433, 121.0449],
  valenzuela: [14.7022, 120.9815],
  marikina: [14.6507, 121.1029],
  'san fernando': [15.0286, 120.6898],
  'santa rosa': [14.3150, 121.1081],
  calapan: [13.4147, 121.1791],
  'puerto princesa': [9.7392, 118.7353],
  tacloban: [11.2543, 125.0090],
  roxas: [11.5854, 122.7544],
  bacolod: [10.6710, 122.9468],
  bacolodcity: [10.6710, 122.9468],
  dumaguete: [9.3069, 123.3044],
  dipolog: [8.5859, 123.3395],
  cotabato: [7.2210, 124.2432],
  pagadian: [7.8271, 123.4314],
  ozamiz: [8.1517, 123.8432],
  ormoc: [11.0061, 124.6057],
  bacoor: [14.4774, 120.9447],
  'san pablo': [14.0795, 121.3299],
  lucena: [13.9373, 121.6156],
  naga: [13.6218, 123.1948],
  legazpi: [13.1352, 123.7464],
  cagayan: [8.4542, 124.6319],
  'cagayan de oro': [8.4542, 124.6319],
  iloilo: [10.7202, 122.5621],
  angeles: [15.1428, 120.5908],
  'angeles city': [15.1428, 120.5908],
  zamboanga: [6.9214, 122.0790],
  'zamboanga city': [6.9214, 122.0790],
  metro: [14.5995, 120.9842],
  ncr: [14.5995, 120.9842],
  'metro manila': [14.5995, 120.9842],
  'national capital region': [14.5995, 120.9842],
  'head office': [14.5547, 121.0245],
  headoffice: [14.5547, 121.0245],
  hq: [14.5547, 121.0245],
  office: [14.5547, 121.0245],
  main: [14.5547, 121.0245],
  central: [14.5547, 121.0245],
  ortigas: [14.5858, 121.0619],
  'ortigas center': [14.5858, 121.0619],
  cubao: [14.6186, 121.0565],
  alabang: [14.4153, 121.0402],
  ayala: [14.5547, 121.0245],
  eastwood: [14.6064, 121.0789],
  clark: [15.1860, 120.5614],
  baguio: [16.4023, 120.5960],
  subic: [14.8088, 120.2716],
  olongapo: [14.8267, 120.2833],
};

const lookupCityConfirm = (rawText) => {
  if (!rawText) return null;
  const txt = String(rawText).toLowerCase().trim();
  if (!txt) return null;
  const entry = Object.entries(PH_CITY_COORDS_CONFIRM).find(([name]) => txt.includes(name));
  if (entry) return { lat: entry[1][0], lng: entry[1][1], name: entry[0] };
  return null;
};

const pickLocationConfirm = (loc, fallbackRecord) => {
  const asNum = (x) => (x === null || x === undefined || x === '' ? null : Number(x));
  const tryObject = (obj) => {
    if (!obj || typeof obj !== 'object') return null;
    if (Array.isArray(obj) && obj.length >= 2) {
      const a = asNum(obj[0]);
      const b = asNum(obj[1]);
      if (typeof a === 'number' && typeof b === 'number' && !Number.isNaN(a) && !Number.isNaN(b)) {
        return { lat: a, lng: b, address: '' };
      }
      return null;
    }
    const lat =
      asNum(obj.latitude) ??
      asNum(obj.lat) ??
      asNum(obj.latitudeDegrees) ??
      asNum(obj.lat_deg) ??
      asNum(obj?.gps?.latitude) ??
      asNum(obj?.coords?.latitude);
    const lng =
      asNum(obj.longitude) ??
      asNum(obj.lng) ??
      asNum(obj.lon) ??
      asNum(obj.longitudeDegrees) ??
      asNum(obj.lon_deg) ??
      asNum(obj?.gps?.longitude) ??
      asNum(obj?.coords?.longitude);
    const address =
      (typeof obj.address === 'string' && obj.address.trim()) ||
      (typeof obj.city_name === 'string' && obj.city_name.trim()) ||
      '';
    if (typeof lat === 'number' && typeof lng === 'number' && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      return { lat, lng, address };
    }
    if (obj.geometry && Array.isArray(obj.geometry.coordinates) && obj.geometry.coordinates.length >= 2) {
      const coords = obj.geometry.coordinates;
      const lngG = asNum(coords[0]);
      const latG = asNum(coords[1]);
      if (typeof latG === 'number' && typeof lngG === 'number' && !Number.isNaN(latG) && !Number.isNaN(lngG)) {
        return { lat: latG, lng: lngG, address };
      }
    }
    return null;
  };
  const candidates = [];
  if (loc) candidates.push(tryObject(loc));
  if (fallbackRecord) {
    candidates.push(tryObject(fallbackRecord.location || fallbackRecord.loc || fallbackRecord.coords));
    const topLevelLat = asNum(fallbackRecord.latitude) ?? asNum(fallbackRecord.lat);
    const topLevelLng = asNum(fallbackRecord.longitude) ?? asNum(fallbackRecord.lng) ?? asNum(fallbackRecord.lon);
    if (typeof topLevelLat === 'number' && typeof topLevelLng === 'number' && !Number.isNaN(topLevelLat) && !Number.isNaN(topLevelLng)) {
      const topAddress =
        (typeof fallbackRecord.location === 'string' && fallbackRecord.location.trim()) ||
        (typeof fallbackRecord.city_name === 'string' && fallbackRecord.city_name.trim()) ||
        '';
      candidates.push({ lat: topLevelLat, lng: topLevelLng, address: topAddress });
    }
    if (fallbackRecord.coords && typeof fallbackRecord.coords.latitude === 'number' && typeof fallbackRecord.coords.longitude === 'number') {
      candidates.push({ lat: fallbackRecord.coords.latitude, lng: fallbackRecord.coords.longitude, address: '' });
    }
  }
  const firstOk = candidates.find((c) => c && typeof c.lat === 'number' && typeof c.lng === 'number') || null;
  if (firstOk) return [firstOk.lat, firstOk.lng, firstOk.address || ''];
  const addressText =
    (loc && typeof loc === 'object' && (typeof loc.address === 'string' ? loc.address : typeof loc.city_name === 'string' ? loc.city_name : '')) ||
    (typeof loc === 'string' ? loc.trim() : '') ||
    (fallbackRecord && typeof fallbackRecord.location === 'string' ? fallbackRecord.location.trim() : '') ||
    (fallbackRecord && typeof fallbackRecord.city_name === 'string' ? fallbackRecord.city_name.trim() : '');
  const city = lookupCityConfirm(addressText);
  if (city) return [city.lat, city.lng, addressText || city.name];
  return [null, null, addressText || ''];
};

const ymdLocalConfirm = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const getTodayFirstLoginFromRecords = (recordsRaw, user) => {
  const userId = user?.id || user?._id || user?.userId || user?.user_id || null;
  const userIds = new Set();
  if (userId != null && userId !== '') userIds.add(String(userId));
  const userName =
    (typeof user?.full_name === 'string' && user.full_name.trim().toLowerCase()) ||
    (typeof user?.name === 'string' && user.name.trim().toLowerCase()) ||
    '';
  const userDisplay =
    (typeof user?.display_name === 'string' && user.display_name.trim().toLowerCase()) ||
    (typeof user?.username === 'string' && user.username.trim().toLowerCase()) ||
    '';
  const ymdToday = ymdLocalConfirm(new Date());
  const all = Array.isArray(recordsRaw) ? recordsRaw : [];
  const mine = all.filter((r) => {
    if (!r) return false;
    const rid =
      r.user_id ||
      r.employee_id ||
      r.employeeId ||
      r.emp_id ||
      (r._id ? String(r._id) : null) ||
      (r.id ? String(r.id) : null);
    if (rid != null && userIds.has(String(rid))) return true;
    const nameA =
      (typeof r.full_name === 'string' && r.full_name.trim().toLowerCase()) ||
      (typeof r.user_name === 'string' && r.user_name.trim().toLowerCase()) ||
      (typeof r.name === 'string' && r.name.trim().toLowerCase()) ||
      '';
    if (userName && nameA && (nameA.includes(userName) || userName.includes(nameA))) return true;
    if (userDisplay && nameA && (nameA.includes(userDisplay) || userDisplay.includes(nameA))) return true;
    return false;
  });
  if (!mine.length) return null;
  const logins = mine.filter((r) => {
    const t = (r.type || '').toLowerCase();
    const a = (r.action || '').toLowerCase();
    if (
      t === 'time_in' || t === 'check_in' || t === 'checkin' || t === 'login' ||
      t === 'punch_in' || t === 'punchin' || t === 'in' || t === 'sign_in' || t === 'signin'
    ) return true;
    if (
      a === 'in' || a === 'time_in' || a === 'check_in' || a === 'login' ||
      a === 'punch_in' || a === 'sign_in'
    ) return true;
    return false;
  });
  const todayLogins = ymdToday
    ? logins.filter((r) => ymdLocalConfirm(new Date(r.timestamp || r.created_at || 0)) === ymdToday)
    : [];
  const pool = todayLogins.length ? todayLogins : logins;
  if (!pool.length) return null;
  const sorted = pool.slice().sort((a, b) => {
    const ta = new Date(a.timestamp || a.created_at || 0).getTime();
    const tb = new Date(b.timestamp || b.created_at || 0).getTime();
    return ta - tb; // earliest first
  });
  const first = sorted[0];
  const [lat, lng, addr] = pickLocationConfirm(first.location || first.loc || {}, first);
  if (!(typeof lat === 'number' && typeof lng === 'number')) return null;
  return {
    lat,
    lng,
    address: addr || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
    city_name: (typeof first.city_name === 'string' && first.city_name.trim()) || addr || '',
    accuracy: typeof first.accuracy === 'number' ? Math.round(first.accuracy) : null,
    source: 'attendance_first_login',
    timestamp: first.timestamp || first.created_at || null,
    isToday: Boolean(todayLogins.length),
  };
};

function MapModal({ isOpen, onConfirm, onClose, axiosInstance, user, saveErrorMsg, clearSaveError }) {
  const { isLight } = useTheme();
  const [mountKey, setMountKey] = useState(0);
  const [location, setLocation] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [address, setAddress] = useState('Loading exact location…');
  const [cityName, setCityName] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [gpsSamples, setGpsSamples] = useState(0);
  const [locationSource, setLocationSource] = useState(null);
  const [attendanceCached, setAttendanceCached] = useState(null);
  const [isRefreshingGps, setIsRefreshingGps] = useState(false);
  const [refreshGpsCounter, setRefreshGpsCounter] = useState(0);
  const [gpsStatusText, setGpsStatusText] = useState('');
  const isDesktop = useMemo(() => {
    try {
      const ua = (navigator.userAgent || '').toLowerCase();
      const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile\s|mobi\b|phone/i.test(ua);
      if (isMobile) return false;
      if (navigator.maxTouchPoints && navigator.maxTouchPoints > 1) return false;
      return true;
    } catch (_) { return true; }
  }, []);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const pingCircleRef1 = useRef(null);
  const pingCircleRef2 = useRef(null);
  const placeholderMarkerRef = useRef(null);
  const invalidatorsRef = useRef([]);
  const timeoutsRef = useRef([]);
  const addressFetchRef = useRef(null);
  const liveWatchIdRef = useRef(null);
  const gpsAcquireStopRef = useRef(null);
  const attendanceFiredRef = useRef(false);
  const initialMapCenterRef = useRef(null);
  const attendanceFirstLoginRef = useRef(null);
  const refreshGpsCounterRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      attendanceFiredRef.current = false;
      setMountKey((k) => k + 1);
    }
  }, [isOpen]);

  useEffect(() => {
    const clearAll = () => {
      invalidatorsRef.current.forEach((id) => clearTimeout(id));
      invalidatorsRef.current = [];
      timeoutsRef.current.forEach((id) => clearTimeout(id));
      timeoutsRef.current = [];
      placeholderMarkerRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
      pingCircleRef1.current = null;
      pingCircleRef2.current = null;
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (_) {}
        mapRef.current = null;
      }
      if (addressFetchRef.current) {
        addressFetchRef.current = null;
      }
      if (liveWatchIdRef.current != null && navigator.geolocation) {
        try { navigator.geolocation.clearWatch(liveWatchIdRef.current); } catch (_) {}
        liveWatchIdRef.current = null;
      }
      if (gpsAcquireStopRef.current) {
        try { gpsAcquireStopRef.current(); } catch (_) {}
        gpsAcquireStopRef.current = null;
      }
      initialMapCenterRef.current = null;
      attendanceFirstLoginRef.current = null;
      refreshGpsCounterRef.current = 0;
    };

    if (!isOpen) {
      clearAll();
      return clearAll;
    }

    setLocation(null);
    setAccuracy(null);
    setAddress('Getting your actual location from device GPS…');
    setCityName('');
    setLocationSource(null);
    setIsLoading(true);
    setError(null);
    setGpsSamples(0);
    setAttendanceCached(null);
    setIsRefreshingGps(false);

    return clearAll;
  }, [isOpen, mountKey]);

  // Attendance history FALLBACK — only used for a fast initial map center flyover
  // while the real device GPS multi-sample settle gate finishes. It NEVER sets
  // the confirmable `location` state: `Confirm Location` stays disabled until
  // actual device GPS settles (per user authority rule).
  useEffect(() => {
    if (!isOpen || !axiosInstance) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/employee_attendance/today');
        const records = Array.isArray(res?.data) ? res.data : [];
        if (cancelled) return;
        setAttendanceCached(records);
        const firstLogin = getTodayFirstLoginFromRecords(records, user || null);
        if (cancelled || !firstLogin) return;
        initialMapCenterRef.current = { lat: firstLogin.lat, lng: firstLogin.lng };
        attendanceFirstLoginRef.current = {
          lat: firstLogin.lat,
          lng: firstLogin.lng,
          address: firstLogin.address || `${firstLogin.lat.toFixed(6)}, ${firstLogin.lng.toFixed(6)}`,
          city_name: firstLogin.city_name || '',
          accuracy: firstLogin.accuracy != null ? firstLogin.accuracy : null,
          source: firstLogin.source || 'attendance_first_login',
        };
        const map = mapRef.current;
        if (map) {
          try {
            map.flyTo([firstLogin.lat, firstLogin.lng], 14, { animate: true, duration: 0.6 });
          } catch (_) {}
        }
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[Confirm Location] attendance preload fallback (fly center only) failed:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, axiosInstance, user, mountKey]);

  useEffect(() => {
    if (!isOpen) return;
    let localMap = null;
    let cancelled = false;

    const init = () => {
      if (cancelled) return;
      if (!mapContainerRef.current) {
        timeoutsRef.current.push(setTimeout(init, 20));
        return;
      }
      if (mapRef.current) return;

      const defaultCenter = [12.8797, 121.7740];
      const defaultZoom = 6;

      try {
        localMap = L.map(mapContainerRef.current, {
          center: defaultCenter,
          zoom: defaultZoom,
          zoomControl: false,
          attributionControl: false,
          trackResize: true,
          worldCopyJump: true,
        });

        L.control.zoom({ position: 'bottomright' }).addTo(localMap);

        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          crossOrigin: true,
        }).addTo(localMap);

        mapRef.current = localMap;

        try {
          const fallbackCenter = initialMapCenterRef.current;
          const fallbackInfo = attendanceFirstLoginRef.current;
          if (fallbackCenter) {
            try { localMap.flyTo([fallbackCenter.lat, fallbackCenter.lng], 14, { animate: true, duration: 0.6 }); } catch (_) {}
            if (fallbackInfo) {
              const fallbackIconHtml = `
                <div style="width:26px;height:26px;border-radius:9999px;background:${isLight ? '#94a3b8' : '#475569'};border:2px solid #fff;box-shadow:0 3px 10px rgba(15,23,42,0.25);opacity:0.55;"></div>
              `;
              const fallbackIcon = L.divIcon({ className: 'gps-fallback-marker', html: fallbackIconHtml, iconSize: [26, 26], iconAnchor: [13, 13] });
              placeholderMarkerRef.current = L.marker([fallbackCenter.lat, fallbackCenter.lng], { icon: fallbackIcon, opacity: 0.55 }).addTo(localMap);
              if (typeof fallbackInfo.address === 'string' && fallbackInfo.address.trim()) {
                try {
                  placeholderMarkerRef.current.bindTooltip(fallbackInfo.address, { direction: 'top', opacity: 0.55, className: 'gps-fallback-tooltip' }).openTooltip();
                } catch (_) {}
              }
            }
          }
        } catch (_) {}

        const schedule = (delay) => {
          const id = setTimeout(() => {
            if (mapRef.current && !cancelled) mapRef.current.invalidateSize(true);
          }, delay);
          invalidatorsRef.current.push(id);
        };
        schedule(20);
        schedule(60);
        schedule(140);
        schedule(320);
        schedule(700);
        schedule(1400);
        schedule(2200);
      } catch (err) {
        console.error('Map init failed:', err);
        setError('Unable to initialize the map. Please refresh and try again.');
        setIsLoading(false);
      }
    };

    timeoutsRef.current.push(setTimeout(init, 0));

    const handleResize = () => {
      if (mapRef.current) mapRef.current.invalidateSize(true);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', handleResize);
      const m = mapRef.current;
      if (m) {
        try { m.stop(); } catch (_) {}
        try {
          if (typeof m._stopAnimations === 'function') m._stopAnimations();
        } catch (_) {}
        try { m.remove(); } catch (_) {}
        mapRef.current = null;
      }
      localMap = null;
      try { markerRef.current = null; } catch (_) {}
      try { circleRef.current = null; } catch (_) {}
      try { pingCircleRef1.current = null; } catch (_) {}
      try { pingCircleRef2.current = null; } catch (_) {}
      try { placeholderMarkerRef.current = null; } catch (_) {}
      // cancel any pending init / invalidateSize scheduled timers we queued
      const buckets = [timeoutsRef.current, invalidatorsRef.current];
      for (const arr of buckets) {
        while (arr.length) {
          try { clearTimeout(arr.pop()); } catch (_) {}
        }
      }
    };
  }, [isOpen, mountKey]);

  useEffect(() => {
    if (!isOpen || !mapRef.current) return;
    const map = mapRef.current;
    if (!location) {
      if (markerRef.current) { try { map.removeLayer(markerRef.current); } catch (_) {} markerRef.current = null; }
      if (circleRef.current) { try { map.removeLayer(circleRef.current); } catch (_) {} circleRef.current = null; }
      if (pingCircleRef1.current) { try { map.removeLayer(pingCircleRef1.current); } catch (_) {} pingCircleRef1.current = null; }
      if (pingCircleRef2.current) { try { map.removeLayer(pingCircleRef2.current); } catch (_) {} pingCircleRef2.current = null; }
      return;
    }

    try {
      if (placeholderMarkerRef.current) {
        try { map.removeLayer(placeholderMarkerRef.current); } catch (_) {}
        placeholderMarkerRef.current = null;
      }
    } catch (_) {}

    try {
      if (locationSource === 'attendance_first_login') {
        map.flyTo([location.lat, location.lng], 14, { animate: false });
      } else {
        map.setView([location.lat, location.lng], 17, { animate: false });
      }
    } catch (_) {}

    const ids = [40, 120, 320].map((d) =>
      setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(true); }, d)
    );
    invalidatorsRef.current.push(...ids);

    try {
      if (markerRef.current) { try { map.removeLayer(markerRef.current); } catch (_) {} markerRef.current = null; }
      if (circleRef.current) { try { map.removeLayer(circleRef.current); } catch (_) {} circleRef.current = null; }
      if (pingCircleRef1.current) { try { map.removeLayer(pingCircleRef1.current); } catch (_) {} pingCircleRef1.current = null; }
      if (pingCircleRef2.current) { try { map.removeLayer(pingCircleRef2.current); } catch (_) {} pingCircleRef2.current = null; }

      const pingColor = isLight ? '#10b981' : '#34d399';

      const personIconHtml = `
        <div style="position:relative;width:44px;height:44px;display:flex;align-items:center;justify-content:center;">
          <span class="gps-ping-ring gps-ping-1" style="position:absolute;inset:0;border-radius:9999px;background:${pingColor};opacity:0.55;will-change:transform,opacity;"></span>
          <span class="gps-ping-ring gps-ping-2" style="position:absolute;inset:0;border-radius:9999px;background:${pingColor};opacity:0.55;will-change:transform,opacity;"></span>
          <div style="position:relative;width:44px;height:44px;border-radius:9999px;background:${pingColor};box-shadow:0 6px 20px rgba(16,185,129,0.45),0 2px 6px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;border:3px solid #fff;">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="#ffffff" aria-hidden="true">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
        </div>
      `;

      const personIcon = L.divIcon({
        className: 'gps-person-marker',
        html: personIconHtml,
        iconSize: [44, 44],
        iconAnchor: [22, 22],
        popupAnchor: [0, -26],
      });

      const marker = L.marker([location.lat, location.lng], { icon: personIcon, zIndexOffset: 1000 }).addTo(map);
      const accuracyLabel = accuracy != null ? ` ±${accuracy.toFixed(0)}m` : '';
      const sourcePretty =
        locationSource === 'attendance_first_login'
          ? 'Today\'s First Login (from employee_attendance)'
          : 'Your Exact Location (device GPS)';
      const popupHtml = `<div style="font-family: ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto;min-width:220px;max-width:300px">
        <div style="font-weight:700;font-size:13px;color:#0f172a;margin-bottom:4px">${sourcePretty}</div>
        <div style="font-size:11.5px;color:#475569;margin-bottom:6px;line-height:1.35">${address}</div>
        <div style="font-size:10.5px;color:#64748b;font-variant-numeric:tabular-nums;margin-bottom:2px">${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}</div>
        ${accuracyLabel ? `<div style="font-size:10.5px;color:#0ea5e9;font-weight:600">GPS Accuracy${accuracyLabel}</div>` : ''}
      </div>`;

      try { marker.bindPopup(popupHtml, { autoPan: true, closeButton: true, autoPanPadding: [24, 24] }).openPopup(); } catch (_) {}

      const radius = accuracy && accuracy > 0 ? Math.max(accuracy, 10) : 30;
      const circleColor = isLight ? '#2563eb' : '#facc15';
      const circle = L.circle([location.lat, location.lng], {
        radius,
        color: circleColor,
        fillColor: circleColor,
        fillOpacity: 0.14,
        weight: 2,
        opacity: 0.9,
        className: 'gps-accuracy-circle',
      }).addTo(map);

      const pingRadius = Math.max(radius, 24);
      const pingCircle1 = L.circle([location.lat, location.lng], {
        radius: pingRadius,
        color: pingColor,
        fillColor: pingColor,
        fillOpacity: 0,
        weight: 2,
        opacity: 1,
        className: 'gps-ping-circle gps-ping-circle-1',
      }).addTo(map);
      const pingCircle2 = L.circle([location.lat, location.lng], {
        radius: pingRadius,
        color: pingColor,
        fillColor: pingColor,
        fillOpacity: 0,
        weight: 2,
        opacity: 1,
        className: 'gps-ping-circle gps-ping-circle-2',
      }).addTo(map);

      markerRef.current = marker;
      circleRef.current = circle;
      pingCircleRef1.current = pingCircle1;
      pingCircleRef2.current = pingCircle2;
    } catch (err) {
      console.warn('Marker update failed:', err);
    }
  }, [isOpen, location, accuracy, address, isLight, locationSource]);

  const runGpsCycle = useCallback(() => {
    if (gpsAcquireStopRef.current) {
      try { gpsAcquireStopRef.current(); } catch (_) {}
      gpsAcquireStopRef.current = null;
    }
    if (liveWatchIdRef.current != null && navigator?.geolocation) {
      try { navigator.geolocation.clearWatch(liveWatchIdRef.current); } catch (_) {}
      liveWatchIdRef.current = null;
    }
    setGpsSamples(0);
    setError(null);
    setIsLoading(true);
    setIsRefreshingGps(true);
    setGpsStatusText('');
    refreshGpsCounterRef.current += 1;

    let stopped = false;

    const handle = acquireFastGeo({
      axiosInstance,
      onUpdate: ({ sample, bestAccuracy, source }) => {
        if (stopped) return;
        setGpsSamples(sample);
        if (bestAccuracy != null) {
          setAccuracy((prev) => {
            if (prev == null) return bestAccuracy;
            return bestAccuracy < prev ? bestAccuracy : prev;
          });
        }
        if (source === 'ip_fallback') setLocationSource(source);
      },
      onStatus: ({ text }) => {
        if (stopped) return;
        if (typeof text === 'string' && text.trim()) setGpsStatusText(text);
      },
      onReady: (payload) => {
        if (stopped) return;
        setLocation({ lat: payload.lat, lng: payload.lng });
        setAccuracy(payload.accuracy);
        setAddress(payload.address || `${payload.lat.toFixed(6)}, ${payload.lng.toFixed(6)}`);
        setCityName(payload.city_name || '');
        setLocationSource(payload.source || 'gps');
        setIsLoading(false);
        setIsRefreshingGps(false);
        setError(null);
        setGpsStatusText('');
        if (placeholderMarkerRef.current && mapRef.current) {
          try { mapRef.current.removeLayer(placeholderMarkerRef.current); } catch (_) {}
          placeholderMarkerRef.current = null;
        }
      },
      onError: (err) => {
        if (stopped) return;
        const msg = (err && err.message) || 'Location could not be acquired.';
        setError(msg);
        setIsLoading(false);
        setIsRefreshingGps(false);
        setGpsStatusText('');
      },
      onTimeout: ({ bestAccuracy, samples, isDesktop: _isDesktop }) => {
        if (stopped) return;
        const accStr = bestAccuracy != null ? `±${bestAccuracy}m` : 'unavailable';
        const msg = _isDesktop
          ? `Location lookup timed out (${samples || 0} samples, best ${accStr}). ` +
            `Ensure location services are enabled for this browser (Settings → Privacy → Location), then tap Update. ` +
            `If you're on an Ethernet-only office network, we'll use a fast IP-based backup location.`
          : `GPS took too long to settle (${samples || 0} samples, best ${accStr}). ` +
            `Step outdoors, enable high-accuracy mode, then tap Update.`;
        setError(msg);
        setIsLoading(false);
        setIsRefreshingGps(false);
        setGpsStatusText('');
      },
    });

    gpsAcquireStopRef.current = handle?.stop || null;

    // IMPORTANT: DO NOT open a duplicate second watchPosition here. The NEW
    // acquireFastGeo pipeline already opens ONE single optimized watch with
    // desktop params. The old code opened a SECOND watch (duplicate work,
    // doubled settle time, impossible to cancel synchronously on close).
    // Duplicate removed 2026-09-30 for desktop GPS delay SLA fix.
    liveWatchIdRef.current = null;

    return () => {
      stopped = true;
      if (handle?.stop) { try { handle.stop(); } catch (_) {} }
      if (liveWatchIdRef.current != null && navigator?.geolocation) {
        try { navigator.geolocation.clearWatch(liveWatchIdRef.current); } catch (_) {}
        liveWatchIdRef.current = null;
      }
    };
  }, [axiosInstance]);

  useEffect(() => {
    if (!isOpen) return undefined;
    return runGpsCycle();
  }, [isOpen, mountKey, refreshGpsCounter, runGpsCycle]);

  if (!isOpen) return null;

  const modalBg = isLight ? 'bg-white' : 'bg-navy-900';
  const modalBorder = isLight ? 'border-slate-200' : 'border-navy-700/50';
  const headerBorder = isLight ? 'border-slate-200' : 'border-navy-700/50';
  const titleText = isLight ? 'text-white' : 'text-white';
  const subText = isLight ? 'text-slate-500' : 'text-slate-400';
  const closeBg = isLight ? 'bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-white' : 'bg-navy-850 text-slate-400 hover:text-white hover:bg-navy-800';
  const accentBg = isLight ? 'bg-emerald-50 text-emerald-600' : 'bg-emerald-500/20 text-emerald-400';
  const accentText = isLight ? 'text-emerald-600' : 'text-emerald-400';
  const cancelBtn = isLight ? 'bg-slate-100 text-white hover:bg-slate-200' : 'bg-navy-800 text-slate-200 hover:bg-navy-700';
  const updateBtn = isLight
    ? 'bg-blue-600 text-white hover:bg-blue-500 disabled:bg-slate-200 disabled:text-slate-400'
    : 'bg-blue-600 text-white hover:bg-blue-500 disabled:bg-navy-800 disabled:text-slate-500';
  const confirmBtn = 'bg-emerald-600 text-white hover:bg-emerald-500';
  const loadingText = isLight ? 'text-slate-500' : 'text-slate-400';
  const mapBg = isLight ? '#e8edf5' : '#0a1734';
  const mapSpinnerBg = isLight ? 'rgba(247,249,252,1)' : 'rgba(11,18,32,1)';
  const infoBorder = isLight ? 'border-slate-200' : 'border-navy-700/50';
  const coordText = isLight ? 'text-slate-400' : 'text-slate-500';
  const gpsPill = isLight ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-blue-500/15 text-blue-300 border-blue-500/30';
  const gpsPillOk = isLight ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
  const accuracyOk = accuracy != null && accuracy <= 30;
  const hasLocation = !!location;
  const pingStroke = isLight ? '#10b981' : '#34d399';

  const geoStatusText = (() => {
    if (gpsStatusText && gpsStatusText.trim()) return gpsStatusText;
    if (error && error.trim()) return null;
    if (hasLocation) return 'Location locked — verify pin placed on map';
    if (isLoading) return isRefreshingGps
      ? 'Re-acquiring location (tap Update if stuck)…'
      : isDesktop
        ? 'Getting your location from your device…'
        : 'Getting your actual location from device GPS…';
    return '';
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <style>{`
        .gps-person-marker {
          background: transparent !important;
          border: 0 !important;
        }
        @keyframes gps-ping-pop {
          0% {
            transform: scale(0.55);
            opacity: 0.95;
          }
          70% {
            opacity: 0;
          }
          100% {
            transform: scale(2.6);
            opacity: 0;
          }
        }
        .gps-ping-ring.gps-ping-1 {
          animation: gps-ping-pop 1.6s cubic-bezier(0.22, 1, 0.36, 1) infinite;
        }
        .gps-ping-ring.gps-ping-2 {
          animation: gps-ping-pop 1.6s cubic-bezier(0.22, 1, 0.36, 1) infinite;
          animation-delay: 0.75s;
        }
        @keyframes gps-ping-circle-grow {
          0% {
            transform: scale(0.6);
            opacity: 1;
          }
          100% {
            transform: scale(2.4);
            opacity: 0;
          }
        }
        .gps-ping-circle-1 path,
        .gps-ping-circle-1 ellipse,
        .gps-ping-circle-1 circle {
          transform-origin: center center;
          transform-box: fill-box;
          animation: gps-ping-circle-grow 1.8s ease-out infinite;
        }
        .gps-ping-circle-2 path,
        .gps-ping-circle-2 ellipse,
        .gps-ping-circle-2 circle {
          transform-origin: center center;
          transform-box: fill-box;
          animation: gps-ping-circle-grow 1.8s ease-out infinite;
          animation-delay: 0.85s;
        }
        .gps-ping-circle-1,
        .gps-ping-circle-2 {
          pointer-events: none !important;
        }
      `}</style>
      <div className={`${modalBg} rounded-2xl overflow-hidden w-full max-w-lg border ${modalBorder} shadow-2xl`}>
        <div className={`flex items-center justify-between p-4 border-b ${headerBorder}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accentBg}`}>
              <svg className={`w-5 h-5 ${accentText}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h3 className={`font-semibold ${titleText}`}>Confirm Location</h3>
              <p className={`text-xs ${subText}`}>Verify your current location</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${closeBg}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={`relative h-72 md:h-80 w-full`} style={{ background: mapSpinnerBg }}>
          {!hasLocation && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-20 px-6 text-center" style={{ background: mapSpinnerBg }}>
              <svg className="w-14 h-14 animate-spin" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="28" cy="28" r="22" stroke={isLight ? 'rgba(16,185,129,0.18)' : 'rgba(52,211,153,0.18)'} strokeWidth="5" />
                <path d="M28 6 C 40.1503 6 50 15.8497 50 28" stroke={pingStroke} strokeWidth="5" strokeLinecap="round" fill="none" />
              </svg>
              <div className="flex flex-col items-center gap-1.5">
                <p className={`text-base font-semibold ${loadingText}`}>
                  {geoStatusText || 'Getting your location from your device…'}
                </p>
                {gpsSamples > 0 && !error && (
                  <p className={`text-xs ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
                    Samples: {gpsSamples} · best {accuracy != null ? `±${accuracy}m` : 'acquiring…'}
                  </p>
                )}
                {attendanceFirstLoginRef.current && !error && (
                  <p className={`text-[10px] ${isLight ? 'text-slate-400/80' : 'text-slate-500/80'}`}>
                    (Showing your today's first login area as a guide while location settles)
                  </p>
                )}
                {locationSource === 'ip_fallback' && !error && (
                  <p className={`text-[10px] ${isLight ? 'text-amber-600/80' : 'text-amber-400/80'}`}>
                    Approximate IP-based location used. Please verify the pin before confirming.
                  </p>
                )}
                {error && (
                  <p className="text-xs font-medium text-amber-400 max-w-xs leading-relaxed">
                    {error}
                  </p>
                )}
              </div>
            </div>
          )}

          <div
            key={`leaflet-mount-${mountKey}`}
            ref={mapContainerRef}
            className="w-full h-full absolute inset-0"
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              opacity: hasLocation ? 1 : 0,
              pointerEvents: hasLocation ? 'auto' : 'none',
              transition: 'opacity 220ms ease-out',
              background: mapBg,
            }}
          />
        </div>

        {hasLocation ? (
          <div className={`p-4 border-t ${infoBorder} space-y-3`}>
            <div className="flex items-start gap-2">
              <svg className={`w-4 h-4 mt-0.5 shrink-0 ${accentText}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              <p className={`text-xs leading-relaxed ${subText}`}>{address}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className={`rounded-xl border ${infoBorder} px-3 py-2`}>
                <p className={`text-[10px] uppercase tracking-wide ${coordText}`}>Latitude</p>
                <p className={`text-sm font-medium ${titleText} font-mono`}>{location.lat.toFixed(7)}°</p>
              </div>
              <div className={`rounded-xl border ${infoBorder} px-3 py-2`}>
                <p className={`text-[10px] uppercase tracking-wide ${coordText}`}>Longitude</p>
                <p className={`text-sm font-medium ${titleText} font-mono`}>{location.lng.toFixed(7)}°</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${accuracyOk ? gpsPillOk : gpsPill}`}>
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 8a3 3 0 100 6 3 3 0 000-6zM12 2a9 9 0 019 9c0 5-9 11-9 11S3 16 3 11a9 9 0 019-9z" />
                </svg>
                {accuracyOk ? `GPS Locked ±${accuracy != null ? accuracy.toFixed(1) : '—'}m` : `GPS Refining ±${accuracy != null ? accuracy.toFixed(1) : '—'}m`}
              </span>
              <span className={`text-[11px] ${coordText}`}>
                {gpsSamples >= 4 ? `Fix stable (${gpsSamples} live samples)` : `Live GPS samples: ${gpsSamples}`}
              </span>
            </div>
          </div>
        ) : null}

        <div className="p-4 pt-0 grid grid-cols-3 gap-3">
          <button
            onClick={onClose}
            className={`px-4 py-2.5 rounded-xl font-medium transition-colors ${cancelBtn}`}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (typeof clearSaveError === 'function') clearSaveError();
              setLocation(null);
              setAddress('Getting your actual location from device GPS…');
              setCityName('');
              setLocationSource(null);
              setAccuracy(null);
              setRefreshGpsCounter((c) => c + 1);
            }}
            disabled={isLoading || isRefreshingGps}
            className={`px-4 py-2.5 rounded-xl font-medium transition-colors
              disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${updateBtn}`}
          >
            {isRefreshingGps || isLoading ? (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M21 12a9 9 0 11-6.219-8.56" strokeLinecap="round" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M4 4v6h6M20 20v-6h-6M5 19a8 8 0 0014.651-3.38M19 5a8 8 0 00-14.651 3.38" />
              </svg>
            )}
            Update
          </button>
          <button
            onClick={() => {
              if (!location) return;
              if (typeof clearSaveError === 'function') clearSaveError();
              onConfirm({ ...location, address, city_name: cityName, accuracy, source: locationSource });
            }}
            disabled={!hasLocation}
            className={`px-4 py-2.5 rounded-xl font-medium transition-colors
              disabled:opacity-40 disabled:cursor-not-allowed
              flex items-center justify-center gap-2 ${confirmBtn}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Confirm Location
          </button>
        </div>

        {saveErrorMsg && typeof saveErrorMsg === 'string' && saveErrorMsg.trim() && (
          <div className={`mt-3 rounded-xl border-2 px-3 py-2.5 text-xs flex items-start gap-2
            ${isLight
              ? 'border-rose-300 bg-rose-50 text-rose-700'
              : 'border-rose-400/30 bg-rose-500/10 text-rose-300'}`}>
            <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M12 9v3m0 3v.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div className="min-w-0 flex-1 leading-snug">
              <p className="font-semibold">Could not save to server</p>
              <p className="mt-0.5 break-words opacity-90">{saveErrorMsg.trim()}</p>
              <p className="mt-1 opacity-80 font-medium">Please retry or check your connection.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SuccessModal({ isOpen, type, timestamp, onClose }) {
  const { isLight } = useTheme();

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(onClose, 4000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !timestamp) return null;

  const modalBg = isLight ? 'bg-white' : 'bg-navy-900';
  const modalBorder = isLight ? 'border-slate-200' : 'border-navy-700/50';
  const titleText = isLight ? 'text-white' : 'text-white';
  const subText = isLight ? 'text-slate-500' : 'text-slate-400';
  const accentBgIn = isLight ? 'bg-emerald-50' : 'bg-emerald-500/20';
  const accentBgOut = isLight ? 'bg-orange-50' : 'bg-orange-500/20';
  const accentIn = isLight ? 'text-emerald-600' : 'text-emerald-400';
  const accentOut = isLight ? 'text-orange-600' : 'text-orange-400';
  const timeBgIn = isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/10 text-emerald-400';
  const timeBgOut = isLight ? 'bg-orange-50 text-orange-700' : 'bg-orange-500/10 text-orange-400';
  const doneBtn = isLight ? 'bg-slate-100 text-white hover:bg-slate-200' : 'bg-navy-800 text-slate-200 hover:bg-navy-700';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className={`${modalBg} rounded-2xl overflow-hidden w-full max-w-sm border ${modalBorder} shadow-2xl text-center p-8`}>
        <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${type === 'in' ? accentBgIn : accentBgOut}`}>
          <svg
            className={`w-10 h-10 ${type === 'in' ? accentIn : accentOut}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h3 className={`text-xl font-bold mb-2 ${titleText}`}>
          {type === 'in' ? 'Timed In Successfully!' : 'Timed Out Successfully!'}
        </h3>

        <p className={`text-sm mb-4 ${subText}`}>
          {type === 'in' ? 'Your time in has been recorded' : 'Your time out has been recorded'}
        </p>

        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${type === 'in' ? timeBgIn : timeBgOut}`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-mono font-semibold">
            {timestamp.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            })}
          </span>
        </div>

        <button
          onClick={onClose}
          className={`mt-6 w-full px-4 py-2.5 rounded-xl font-medium transition-colors ${doneBtn}`}
        >
          Done
        </button>
      </div>
    </div>
  );
}

function RecordingOverlayModal({ isOpen, action }) {
  const { isLight } = useTheme();

  if (!isOpen) return null;

  const effectiveAction = action && (action === 'in' || action === 'out') ? action : 'in';

  const modalBg = isLight ? 'bg-white' : 'bg-navy-900';
  const modalBorder = isLight ? 'border-slate-200' : 'border-navy-700/50';
  const titleText = isLight ? 'text-slate-900' : 'text-white';
  const subText = isLight ? 'text-slate-500' : 'text-slate-400';
  const accentBg = effectiveAction === 'in'
    ? (isLight ? 'bg-emerald-50' : 'bg-emerald-500/20')
    : (isLight ? 'bg-orange-50' : 'bg-orange-500/20');
  const accent = effectiveAction === 'in'
    ? (isLight ? 'text-emerald-600' : 'text-emerald-400')
    : (isLight ? 'text-orange-600' : 'text-orange-400');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 pointer-events-none">
      <div className={`${modalBg} rounded-2xl overflow-hidden w-full max-w-sm border ${modalBorder} shadow-2xl text-center p-8`}>
        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${accentBg}`}>
          <svg className={`w-9 h-9 ${accent} animate-spin`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M21 12a9 9 0 11-6.219-8.56" strokeLinecap="round" />
          </svg>
        </div>

        <h3 className={`text-xl font-bold mb-2 ${titleText}`}>
          {effectiveAction === 'out' ? 'Recording Time Out…' : 'Recording Time In…'}
        </h3>

        <p className={`text-sm mb-2 ${subText}`}>
          Recording your activity to the server. Please wait…
        </p>

        <p className={`text-xs ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
          This usually takes 2–5 seconds while we upload your selfie and premises photos.
        </p>
      </div>
    </div>
  );
}

function TodaysActivity({ activities }) {
  const { isLight } = useTheme();

  const statusBadgeStyle = (status) => {
    const s = (status || '').trim();
    if (!s) return null;
    if (s === 'On time') {
      return isLight
        ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
        : 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/20';
    }
    if (s.startsWith('Late')) {
      return isLight
        ? 'bg-amber-100 text-amber-700 border border-amber-200'
        : 'bg-amber-500/15 text-amber-300 border border-amber-400/20';
    }
    if (s.startsWith('Undertime')) {
      return isLight
        ? 'bg-rose-100 text-rose-700 border border-rose-200'
        : 'bg-rose-500/15 text-rose-300 border border-rose-400/20';
    }
    return isLight
      ? 'bg-slate-100 text-slate-600 border border-slate-200'
      : 'bg-slate-500/15 text-slate-300 border border-slate-400/20';
  };

  const cardBg = isLight ? 'bg-white/78 border-slate-200' : 'bg-navy-900/60 border-navy-700/50';
  const titleText = isLight ? 'text-white' : 'text-slate-300';
  const iconTitle = isLight ? 'text-blue-600' : 'text-blue-400';
  const emptyIconBg = isLight ? 'bg-slate-100 text-slate-400' : 'bg-navy-850/50 text-slate-500';
  const emptySub = isLight ? 'text-slate-500' : 'text-slate-500';
  const emptyHint = isLight ? 'text-slate-400' : 'text-slate-600';
  const itemBg = isLight ? 'bg-slate-50 border-slate-200' : 'bg-navy-850/30 border-navy-700/30';
  const accentInBg = isLight ? 'bg-emerald-50' : 'bg-emerald-500/20';
  const accentOutBg = isLight ? 'bg-orange-50' : 'bg-orange-500/20';
  const accentInText = isLight ? 'text-emerald-600' : 'text-emerald-400';
  const accentOutText = isLight ? 'text-orange-600' : 'text-orange-400';
  const timelineLine = isLight ? 'bg-slate-200' : 'bg-navy-700/50';
  const dateText = isLight ? 'text-slate-500' : 'text-slate-400';
  const timeText = isLight ? 'text-slate-900' : 'text-white';
  const inputText = isLight ? 'text-white' : 'text-slate-300';
  const metaText = isLight ? 'text-white' : 'text-slate-300';
  const metaSubtle = isLight ? 'text-slate-500' : 'text-slate-400';
  const locIcon = isLight ? 'text-slate-400' : 'text-slate-500';
  const remarksLabel = isLight ? 'text-slate-400' : 'text-slate-500';
  const locLabel = isLight ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className={`backdrop-blur rounded-2xl p-5 border ${cardBg}
      ${isLight
        ? 'shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)] border-white/70 ring-1 ring-slate-200/60'
        : 'shadow-[0_10px_40px_-10px_rgba(0,0,0,0.55)] border-white/10 ring-1 ring-white/5'}`}>
      <div className="flex items-center gap-2 mb-4">
        <svg className={`w-4 h-4 ${iconTitle}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <h3 className={`text-sm font-semibold uppercase tracking-wider ${titleText}`}>
          Today's Activity
        </h3>
      </div>

      {activities.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${emptyIconBg}`}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className={`text-sm ${emptySub}`}>No activity recorded yet</p>
          <p className={`text-xs mt-1 ${emptyHint}`}>Tap Time In to start your day</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity, index) => {
            const isIn = activity.type === 'in';
            const displayType = activity.displayType || (isIn ? 'Time In' : 'Time Out');
            const displayDate = activity.displayDate || formatDateAsRequested(activity.timestamp);
            const displayTime = activity.displayTime || formatTime12AmPm(activity.timestamp);
            const displayFullName = activity.displayFullName || '';
            const displayBranch = activity.displayBranch || '';
            const displayStatus = activity.displayStatus || '';
            const displayLocation = activity.displayLocation || activity.cityName || '';
            const displayRemarks = activity.displayRemarks || activity.userInput || '';
            const statusClass = statusBadgeStyle(displayStatus);

            return (
              <div
                key={index}
                className={`relative flex items-start gap-3 p-4 rounded-xl border ${itemBg}
                  ${isLight
                    ? 'shadow-[0_6px_18px_-10px_rgba(15,23,42,0.18)] border-white/80 ring-1 ring-slate-200/70'
                    : 'shadow-[0_6px_24px_-8px_rgba(0,0,0,0.6)] border-white/10 ring-1 ring-white/5'}`}>
              >
                <div className="flex flex-col items-center pt-0.5">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                    isIn ? accentInBg : accentOutBg
                  }`}>
                    {isIn ? (
                      <svg className={`w-4 h-4 ${accentInText}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                    ) : (
                      <svg className={`w-4 h-4 ${accentOutText}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H4a3 3 0 01-3-3V7a3 3 0 013-3h6a3 3 0 013 3v1" />
                      </svg>
                    )}
                  </div>
                  {index < activities.length - 1 && (
                    <div className={`w-[1px] h-full mt-1 ${timelineLine}`} />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-semibold uppercase tracking-wider ${
                        isIn
                          ? isLight
                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                            : 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/20'
                          : isLight
                            ? 'bg-orange-100 text-orange-700 border border-orange-200'
                            : 'bg-orange-500/15 text-orange-300 border border-orange-400/20'
                      }`}
                    >
                      {displayType}
                    </span>
                    <div className="flex items-center gap-2 text-right">
                      <span className={`text-xs font-medium ${dateText}`}>{displayDate}</span>
                      <span className={`text-sm font-mono font-bold ${timeText}`}>{displayTime}</span>
                    </div>
                  </div>

                  <div className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-2 pb-1`}>
                    {displayBranch ? (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <svg className={`w-3.5 h-3.5 shrink-0 ${metaSubtle}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className={`text-xs font-medium truncate ${metaText}`}>{displayBranch}</span>
                      </div>
                    ) : null}
                    {statusClass ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${statusClass}`}>
                        {displayStatus}
                      </span>
                    ) : null}
                  </div>

                  {displayRemarks ? (
                    <div className="mb-2">
                      <div className={`text-[10px] uppercase tracking-wider font-semibold mb-0.5 ${remarksLabel}`}>
                        Remarks
                      </div>
                      <p className={`text-sm whitespace-pre-wrap break-words ${inputText}`}>
                        {displayRemarks}
                      </p>
                    </div>
                  ) : null}

                  {displayLocation ? (
                    <div>
                      <div className={`text-[10px] uppercase tracking-wider font-semibold mb-0.5 ${locLabel}`}>
                        Location
                      </div>
                      <div className="flex items-start gap-1.5">
                        <svg className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${locIcon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className={`text-xs break-words ${metaText}`}>{displayLocation}</span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ----- Reusable small components for MORE dropdown + modals -----
function SimpleModal({ isOpen, title, onClose, children, maxWidthClass = 'max-w-lg' }) {
  const { isLight } = useTheme();
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  const overlay = 'fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm animate-fade-in';
  const bg = isLight ? 'bg-white' : 'bg-navy-900';
  const border = isLight ? 'border-slate-200' : 'border-navy-700/60';
  const textTitle = isLight ? 'text-slate-900' : 'text-white';
  const subText = isLight ? 'text-slate-500' : 'text-slate-400';
  return (
    <div className={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className={`relative w-full ${maxWidthClass} max-h-[88vh] overflow-hidden rounded-2xl ${bg} border ${border} shadow-2xl flex flex-col`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-inherit">
          <h3 className={`text-lg font-semibold truncate ${textTitle}`}>{title || ''}</h3>
          <button
            type="button"
            onClick={() => onClose?.()}
            className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-colors
              ${isLight ? 'hover:bg-slate-100 text-slate-500' : 'hover:bg-navy-800 text-slate-400'}`}
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={`flex-1 overflow-y-auto px-5 py-4 text-sm ${subText}`}>
          {children}
        </div>
      </div>
    </div>
  );
}

function SimpleDropdown({ isOpen, onClose, buttonNode, items }) {
  const { isLight } = useTheme();
  const wrapRef = useRef(null);
  const didFirstOpenRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      didFirstOpenRef.current += 1;
    }
    const onDocDown = (e) => {
      if (!wrapRef.current) return;
      const t = e.target;
      if (wrapRef.current.contains(t)) return;
      onClose?.();
    };
    const onKey = (e) => { if (isOpen && e.key === 'Escape') onClose?.(); };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  const panelBg = isLight ? 'bg-white border-slate-200 text-slate-700' : 'bg-navy-850/95 border-navy-700/70 text-slate-200';
  const itemHover = isLight ? 'hover:bg-slate-100' : 'hover:bg-navy-700/60';
  const separator = isLight ? 'border-slate-100' : 'border-white/5';

  return (
    <div className="relative flex flex-col items-center w-full" ref={wrapRef}>
      {buttonNode}
      {isOpen && (
        <div
          role="menu"
          className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 min-w-[220px] rounded-2xl p-1.5 border shadow-2xl z-40 ${panelBg}
            origin-top animate-in fade-in slide-in-from-top-2 duration-150`}
        >
          {(items || []).map((it, idx) => (
            <div key={idx}>
              {idx > 0 && <div className={`my-1 border-t ${separator}`} />}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); onClose?.(); it.onClick?.(); }}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-left text-sm font-medium transition-colors ${itemHover}`}
              >
                {typeof it.icon === 'function' ? it.icon() : it.icon}
                <span className="flex-1 truncate">{it.label}</span>
                {it.chevron !== false && (
                  <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WorkSchedule({ schedule, timeInRecord, activities = [] }) {
  const { isLight } = useTheme();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const parseTime = (t) => {
    const [h, m] = t.split(':').map(Number);
    const d = new Date(now);
    d.setHours(h, m, 0, 0);
    return d;
  };

  const shiftStart = parseTime(schedule.shiftStart);
  const shiftEnd = parseTime(schedule.shiftEnd);

  const lunchBreakMs = 1 * 60 * 60 * 1000;
  const totalShiftMs = shiftEnd.getTime() - shiftStart.getTime() - lunchBreakMs;

  const lunchStart = new Date(shiftStart.getTime() + (shiftEnd.getTime() - shiftStart.getTime()) / 2 - lunchBreakMs / 2);
  const lunchEnd = new Date(lunchStart.getTime() + lunchBreakMs);

  const timeToProgress = (date) => {
    if (date >= shiftEnd) return 100;
    if (date <= shiftStart) return 0;

    const raw = date.getTime() - shiftStart.getTime();
    let effective = raw;
    if (date >= lunchEnd) {
      effective = raw - lunchBreakMs;
    } else if (date >= lunchStart) {
      effective = lunchStart.getTime() - shiftStart.getTime();
    }
    return Math.min(100, Math.max(0, (effective / totalShiftMs) * 100));
  };

  const progress = timeToProgress(now);

  const formatTime12 = (t) => {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${pad(m)} ${ampm}`;
  };

  const formatStamp = (d) =>
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

  const totalHours = totalShiftMs / (1000 * 60 * 60);
  const elapsedHours = (progress / 100) * totalHours;
  const remainingHours = Math.max(0, totalHours - elapsedHours);

  const formatDuration = (hrs) => {
    const h = Math.floor(hrs);
    const m = Math.floor((hrs - h) * 60);
    return `${h}h ${m}m`;
  };

  const getProgressColor = () => {
    if (progress >= 100) return 'from-emerald-500 to-emerald-400';
    if (progress >= 75) return isLight ? 'from-blue-500 to-emerald-400' : 'from-blue-500 to-emerald-400';
    if (progress >= 50) return isLight ? 'from-blue-500 to-blue-400' : 'from-blue-500 to-blue-400';
    if (progress >= 25) return isLight ? 'from-indigo-500 to-blue-400' : 'from-indigo-500 to-blue-400';
    return isLight ? 'from-indigo-600 to-indigo-400' : 'from-indigo-600 to-indigo-400';
  };

  const cardBg = isLight ? 'bg-white/78 border-slate-200' : 'bg-navy-900/60 border-navy-700/50';
  const titleText = isLight ? 'text-white' : 'text-slate-300';
  const shiftStartText = isLight ? 'text-emerald-600' : 'text-emerald-400';
  const shiftEndText = isLight ? 'text-orange-600' : 'text-orange-400';
  const startLine = isLight ? 'from-emerald-400/40' : 'from-emerald-500/40';
  const endLine = isLight ? 'from-orange-400/40' : 'from-orange-500/40';
  const labelSub = isLight ? 'text-slate-500' : 'text-slate-500';
  const labelHint = isLight ? 'text-slate-400 italic' : 'text-slate-600 italic';
  const trackBg = isLight ? 'bg-slate-200' : 'bg-navy-800/80';
  const percentText = isLight ? 'text-slate-500' : 'text-slate-500';
  const legendColor = isLight ? 'text-slate-400' : 'text-slate-500';
  const infoBorder = isLight ? 'border-slate-200' : 'border-navy-700/50';
  const infoText = isLight ? 'text-slate-500' : 'text-slate-400';
  const infoAccent = isLight ? 'text-emerald-600' : 'text-emerald-400';

  return (
    <div className={`backdrop-blur rounded-2xl p-5 border ${cardBg}
      ${isLight
        ? 'shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)] border-white/70 ring-1 ring-slate-200/60'
        : 'shadow-[0_10px_40px_-10px_rgba(0,0,0,0.55)] border-white/10 ring-1 ring-white/5'}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-2 h-2 rounded-full ${isLight ? 'bg-emerald-500' : 'bg-emerald-400'} animate-pulse`} />
        <h3 className={`text-sm font-semibold uppercase tracking-wider ${titleText}`}>
          Work Schedule
        </h3>
      </div>

      <div className="flex justify-between items-center mb-3">
        <div className="text-center">
          <p className={`text-xs mb-1 ${labelSub}`}>Shift Start</p>
          <p className={`text-lg font-mono font-bold ${shiftStartText}`}>
            {formatTime12(schedule.shiftStart)}
          </p>
        </div>
        <div className="flex-1 mx-4 flex flex-col items-center gap-0.5">
          <div className="flex items-center gap-2 w-full">
            <div className={`h-[1px] flex-1 bg-gradient-to-r ${startLine} to-transparent`} />
            <span className={`text-xs font-medium ${labelSub}`}>{totalHours}hrs</span>
            <div className={`h-[1px] flex-1 bg-gradient-to-l ${endLine} to-transparent`} />
          </div>
          <span className={`text-[10px] ${labelHint}`}>1hr lunch</span>
        </div>
        <div className="text-center">
          <p className={`text-xs mb-1 ${labelSub}`}>Shift End</p>
          <p className={`text-lg font-mono font-bold ${shiftEndText}`}>
            {formatTime12(schedule.shiftEnd)}
          </p>
        </div>
      </div>

      <div className="relative mb-3">
        <div className="relative h-3 py-1">
          <div className={`h-3 rounded-full overflow-hidden ${trackBg}`}>
            <div
              className={`h-full bg-gradient-to-r ${getProgressColor()} rounded-full transition-all duration-1000 ease-linear relative`}
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-white/10 animate-pulse rounded-full" />
            </div>
          </div>

          <div className="absolute inset-x-0 top-0 h-3 pointer-events-none">
            {activities.map((activity, i) => {
              const pos = timeToProgress(activity.timestamp);
              const isIn = activity.type === 'in';
              return (
                <div
                  key={i}
                  className="absolute group pointer-events-auto"
                  style={{
                    left: `${pos}%`,
                    top: '-4px',
                    transform: 'translateX(-50%)',
                  }}
                >
                  <div
                    className={`w-[3px] h-5 rounded-full shadow-lg ${
                      isIn
                        ? 'bg-emerald-400 shadow-emerald-400/60'
                        : 'bg-red-500 shadow-red-500/60'
                    }`}
                  />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-20">
                    <div className={`whitespace-nowrap px-2 py-1 rounded-lg border text-[10px] shadow-xl ${isLight ? 'bg-white border-slate-200' : 'bg-navy-950 border-navy-700'}`}>
                      <span className={isIn ? (isLight ? 'text-emerald-600' : 'text-emerald-400') : (isLight ? 'text-red-600' : 'text-red-400')}>
                        {isIn ? 'IN' : 'OUT'}
                      </span>
                      <span className={`ml-1 font-mono ${isLight ? 'text-white' : 'text-slate-300'}`}>
                        {formatStamp(activity.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-between mt-2.5">
          <span className={`text-xs ${percentText}`}>{progress.toFixed(1)}%</span>
          <span className={`text-xs ${percentText}`}>
            {progress >= 100 ? 'Shift Complete' : `${formatDuration(remainingHours)} remaining`}
          </span>
        </div>
      </div>

      {activities.length > 0 && (
        <div className="flex items-center gap-4 mt-2">
          <div className="flex items-center gap-1.5">
            <div className="w-[3px] h-3 rounded-full bg-emerald-400" />
            <span className={`text-[10px] ${legendColor}`}>Time In</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-[3px] h-3 rounded-full bg-red-500" />
            <span className={`text-[10px] ${legendColor}`}>Time Out</span>
          </div>
        </div>
      )}

      {timeInRecord && (
        <div className={`mt-3 pt-3 border-t flex items-center gap-2 ${infoBorder}`}>
          <svg className={`w-4 h-4 ${infoAccent}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className={`text-xs ${infoText}`}>
            Timed in at{' '}
            <span className={`font-semibold ${infoAccent}`}>
              {formatStamp(timeInRecord)}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

export default function EmployeeLandingPage({ user, axiosInstance, onLogout }) {
  const { isLight } = useTheme();
  const [currentAction, setCurrentAction] = useState(null);
  const [modalStep, setModalStep] = useState(null);
  const [selfiePhoto, setSelfiePhoto] = useState(null);
  const [premisesPhoto, setPremisesPhoto] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [timeInRecord, setTimeInRecord] = useState(null);
  const [timeOutRecord, setTimeOutRecord] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastAction, setLastAction] = useState('in');
  const [lastTimestamp, setLastTimestamp] = useState(null);
  const [activities, setActivities] = useState([]);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isIntro, setIsIntro] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recordingModalOpen, setRecordingModalOpen] = useState(false);
  const [recordingModalAction, setRecordingModalAction] = useState(null);
  const [recordingStartTs, setRecordingStartTs] = useState(null);

  // More icon dropdown + 3 feature modals
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [logHistoryOpen, setLogHistoryOpen] = useState(false);
  const [payslipOpen, setPayslipOpen] = useState(false);
  const [itineraryOpen, setItineraryOpen] = useState(false);
  const [logHistoryLoading, setLogHistoryLoading] = useState(false);
  const [logHistoryError, setLogHistoryError] = useState(null);
  const [logHistoryDays, setLogHistoryDays] = useState([]);
  const [itineraryLoading, setItineraryLoading] = useState(false);
  const [itineraryError, setItineraryError] = useState(null);
  const [todayItineraries, setTodayItineraries] = useState([]);
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [remarksModalKind, setRemarksModalKind] = useState('completed'); // 'completed' | 'cancelled'
  const [remarksTargetItId, setRemarksTargetItId] = useState(null);
  const [remarksText, setRemarksText] = useState('');
  const [remarksSubmitting, setRemarksSubmitting] = useState(false);
  const [saveErrorMsg, setSaveErrorMsg] = useState('');
  const moreButtonRef = useRef(null);
  const moreDropdownRef = useRef(null);
  const [logHistoryFired, setLogHistoryFired] = useState(0);
  const [itineraryFired, setItineraryFired] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setIsIntro(false), 2600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!axiosInstance) return undefined;
    // Page-level background prefetch of location so Confirm Location modal
    // gets a cache hit 95% of the time (<=300 ms to show a pin). Runs once
    // on Employee Landing Page mount, not when the modal opens.
    //
    // DEPENDENCY = [] (empty) is deliberate. The axiosInstance prop is NOT
    // guaranteed to be referentially stable between renders (the parent
    // NewAdminDashboard may recreate it on every pass). If we listed it as a
    // dependency here, every parent re-render (e.g., the 1 Hz clock tick in
    // AnalogClock, or any toast state change) would cancel + restart the
    // prefetch pipeline before the cache could populate, spawning N parallel
    // watchPosition callers + their N 5-second IP fallback timers, which
    // then hit /api/ip-geolocation concurrently — a thundering herd that
    // starves the backend uvicorn worker pool and causes session refresh /
    // attendance hydration calls to queue until 30s Axios timeout (the exact
    // regression shown in the 2026-09-30 screenshot).
    try {
      const cleanup = prefetchFastGeo({ axiosInstance });
      return cleanup;
    } catch (_) {
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTodaysRecords = useCallback(async () => {
    if (!axiosInstance) return undefined;
    try {
      // 8-second timeout for attendance hydrate. Default 30-second axios timeout
      // caused the "Hydrate employee attendance failed: timeout 30000ms"
      // warnings whenever the backend was stuck (e.g. staging 503). With 8s we
      // surface today's records quickly or fall back gracefully.
      const res = await axiosInstance.get('/employee_attendance/today', { timeout: 8000 });
      const records = Array.isArray(res?.data) ? res.data : [];

      const mappedActivities = records
        .filter((r) => r.action === 'in' || r.action === 'out')
        .map((r) => {
          const tsObj = new Date(r.timestamp);
          const prettyAction = typeof r.type === 'string' && r.type.trim()
            ? r.type.trim()
            : (r.action === 'in' ? 'Time In' : 'Time Out');
          const cityFallback = (r.latitude != null && r.longitude != null)
            ? `${Number(r.latitude).toFixed(4)}, ${Number(r.longitude).toFixed(4)}`
            : '';
          const displayLocation =
            (typeof r.location === 'string' && r.location.trim()) ||
            (typeof r.city_name === 'string' && r.city_name.trim()) ||
            cityFallback;
          const displayRemarks =
            (typeof r.remarks === 'string' && r.remarks.trim()) ||
            (typeof r.location_note === 'string' && r.location_note.trim()) ||
            '';
          const displayFullName =
            (typeof r.full_name === 'string' && r.full_name.trim()) ||
            (typeof r.user_name === 'string' && r.user_name.trim()) ||
            '';
          const displayBranch =
            (typeof r.branch === 'string' && r.branch.trim()) ||
            '';
          const displayDate =
            (typeof r.date === 'string' && r.date.trim()) ||
            formatDateAsRequested(tsObj);
          const displayTime =
            (typeof r.time === 'string' && r.time.trim()) ||
            formatTime12AmPm(tsObj);
          const displayStatus =
            (typeof r.status === 'string' && r.status.trim()) || '';
          const normalizedType = r.action === 'in' ? 'in' : 'out';
          return {
            type: normalizedType,
            timestamp: tsObj,
            userInput: displayRemarks,
            cityName: displayLocation,
            displayDate,
            displayTime,
            displayType: prettyAction,
            displayFullName,
            displayBranch,
            displayStatus,
            displayRemarks,
            displayLocation,
          };
        })
        .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      setActivities(mappedActivities);

      const sortedAsc = [...records]
        .filter((r) => r.action === 'in' || r.action === 'out')
        .map((r) => ({ ...r, tsObj: new Date(r.timestamp) }))
        .sort((a, b) => a.tsObj.getTime() - b.tsObj.getTime());

      let hydratedTimeIn = null;
      let hydratedTimeOut = null;
      if (sortedAsc.length > 0) {
        const lasts = {};
        for (const r of sortedAsc) {
          lasts[r.action] = r;
        }
        const latestIn = lasts['in'];
        const latestOut = lasts['out'];
        const inTs = latestIn ? latestIn.tsObj.getTime() : -Infinity;
        const outTs = latestOut ? latestOut.tsObj.getTime() : -Infinity;

        hydratedTimeIn = latestIn ? latestIn.tsObj : null;
        if (latestOut && outTs > inTs) {
          hydratedTimeOut = latestOut.tsObj;
        } else {
          hydratedTimeOut = null;
        }
      }
      setTimeInRecord(hydratedTimeIn);
      setTimeOutRecord(hydratedTimeOut);
      return { records: sortedAsc };
    } catch (err) {
      console.warn('Hydrate employee attendance failed:', err);
      return undefined;
    }
  }, [axiosInstance]);

  useEffect(() => {
    if (!axiosInstance) return;
    let mounted = true;
    loadTodaysRecords().then(() => { if (!mounted) return; }).catch(() => {});
    return () => { mounted = false; };
  }, [axiosInstance, user?.id, loadTodaysRecords]);

  const intro = (animClass, delay, baseClass = '') =>
    isIntro
      ? {
          className: `intro-item ${animClass} ${baseClass}`,
          style: { animationDelay: `${delay}s` },
        }
      : { className: baseClass, style: undefined };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    setModalStep(null);
    setCurrentAction(null);
    setSelfiePhoto(null);
    setPremisesPhoto(null);
    setUserInput('');
    setShowSuccess(false);
    if (onLogout) onLogout();
  };

  const handleTimeAction = (type) => {
    setSaveErrorMsg('');
    setCurrentAction(type);
    setSelfiePhoto(null);
    setPremisesPhoto(null);
    setUserInput('');
    setModalStep('greeting');
  };

  const handleGreetingContinue = (input) => {
    setUserInput(input);
    setModalStep('camera-selfie');
  };

  const handleSelfieCaptured = (photo) => {
    setSelfiePhoto(photo);
    setModalStep('camera-premises');
  };

  const handlePremisesCaptured = (photo) => {
    setPremisesPhoto(photo);
    setModalStep('map');
  };

  const handleCancel = () => {
    setSaveErrorMsg('');
    setModalStep(null);
    setCurrentAction(null);
    setSelfiePhoto(null);
    setPremisesPhoto(null);
    setUserInput('');
  };

  const submitAttendance = useCallback(async (payload) => {
    if (!axiosInstance) {
      return { ok: false, error: 'App not ready — please wait a moment and try again.' };
    }

    const guardUserId = String(user?.id ?? user?.user_id ?? '').trim();
    const guardAction = typeof payload?.action === 'string' ? payload.action.trim().toLowerCase() : '';
    const guardCheck = _attendanceSubmitCanGo(guardUserId, guardAction);
    if (!guardCheck.ok) {
      const waitSecs = Math.ceil(guardCheck.remainingMs / 1000);
      const elapsedSecs = Math.max(0, 60 - waitSecs);
      const prettyType = guardAction === 'out' ? 'Time Out' : 'Time In';
      return {
        ok: false,
        error: guardUserId
          ? `Another ${prettyType} was recorded just ${elapsedSecs}s ago. Please wait ${waitSecs}s before attempting again.`
          : 'Please wait a moment before attempting again.'
      };
    }

    try {
      setIsSubmitting(true);
      const resp = await axiosInstance.post('/employee_attendance', payload);
      _attendanceSubmitMarkSent(guardUserId, guardAction);
      await loadTodaysRecords();
      return { ok: true, record: (resp && resp.data) ? resp.data : null };
    } catch (err) {
      _attendanceSubmitClearMark(guardUserId, guardAction);
      const msg =
        (err && err.response && err.response.data && typeof err.response.data === 'string')
          ? err.response.data
          : (err && err.response && err.response.data && err.response.data.detail)
            ? String(err.response.data.detail)
            : (err && typeof err.message === 'string')
              ? err.message
              : 'Could not save to server. Please check your connection and try again.';
      return { ok: false, error: msg };
    } finally {
      setIsSubmitting(false);
    }
  }, [axiosInstance, loadTodaysRecords, user?.id, user?.user_id]);

  const handleLocationConfirmed = useCallback(
    async (locationInfo) => {
      if (!axiosInstance || recordingModalOpen || isSubmitting) return;

      const clientNow = new Date();
      const lat = locationInfo?.lat;
      const lng = locationInfo?.lng;
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        console.warn('Invalid location confirm payload:', locationInfo);
        return;
      }

      setRecordingModalAction(currentAction);
      setRecordingStartTs(clientNow);
      setSaveErrorMsg('');
      setRecordingModalOpen(true);
      setModalStep(null);

      const resolvedCity =
        (typeof locationInfo?.city_name === 'string' && locationInfo.city_name.trim())
          ? locationInfo.city_name
          : '';

      const attendancePayload = {
        action: currentAction,
        timestamp: clientNow.toISOString(),
        selfie_photo: selfiePhoto,
        premises_photo: premisesPhoto,
        location_note: userInput,
        remarks: userInput,
        latitude: lat,
        longitude: lng,
        city_name: resolvedCity,
        location: resolvedCity,
        employee_id: user?.id,
      };

      const res = await submitAttendance(attendancePayload);

      if (!res.ok) {
        setSaveErrorMsg(res.error || 'Save failed. Please try again or check your connection.');
        setRecordingModalOpen(false);
        setModalStep('map');
        return;
      }

      setSaveErrorMsg('');

      const serverTimestampRaw = (res.record && res.record.timestamp) ? res.record.timestamp : null;
      const savedTime = serverTimestampRaw ? new Date(serverTimestampRaw) : clientNow;

      if (currentAction === 'in') {
        setTimeInRecord(savedTime);
      } else {
        setTimeOutRecord(savedTime);
      }

      const prettyType = currentAction === 'in' ? 'Time In' : 'Time Out';
      const optimisticDisplayLocation =
        resolvedCity ||
        (typeof locationInfo?.address === 'string' && locationInfo.address.trim()) ||
        `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      const userBranch = (typeof user?.branch === 'string' && user.branch.trim()) || '';
      const userFullName =
        (typeof user?.full_name === 'string' && user.full_name.trim()) ||
        (typeof user?.name === 'string' && user.name.trim()) ||
        '';
      const newActivity = {
        type: currentAction,
        timestamp: savedTime,
        userInput: userInput,
        cityName: optimisticDisplayLocation,
        displayDate: formatDateAsRequested(savedTime),
        displayTime: formatTime12AmPm(savedTime),
        displayType: prettyType,
        displayFullName: userFullName,
        displayBranch: userBranch,
        displayStatus: '',
        displayRemarks: userInput,
        displayLocation: optimisticDisplayLocation,
      };

      if (!resolvedCity && !(typeof locationInfo?.address === 'string' && locationInfo.address.trim())) {
        fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=10`
        )
          .then((res) => res.json())
          .then((data) => {
            const city =
              data?.address?.city ||
              data?.address?.town ||
              data?.address?.municipality ||
              data?.address?.county ||
              data?.address?.state ||
              `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
            const targetTsMs = savedTime.getTime();
            setActivities((prev) =>
              prev.map((a) =>
                a && a.timestamp && typeof a.timestamp.getTime === 'function'
                  && a.timestamp.getTime() === targetTsMs
                  && (a.type === currentAction || a.displayType === prettyType)
                  ? { ...a, cityName: city }
                  : a
              )
            );
          })
          .catch(() => {});
      }

      setLastAction(currentAction);
      setLastTimestamp(savedTime);
      setModalStep(null);
      setCurrentAction(null);
      setSelfiePhoto(null);
      setPremisesPhoto(null);
      setUserInput('');
      setRecordingModalOpen(false);
      setShowSuccess(true);
    },
    [axiosInstance, recordingModalOpen, isSubmitting, currentAction, selfiePhoto, premisesPhoto, userInput, user, submitAttendance]
  );

  // -------- RouteCore itinerary helpers (inline parity with @routecore/core) --------
  const ordinalUp = useCallback((n) => {
    const num = Number.isFinite(+n) ? Math.max(1, Math.floor(+n)) : 1;
    const suffixes = ['TH', 'ST', 'ND', 'RD'];
    const v = num % 100;
    const pick = (v >= 11 && v <= 13) ? 0 : (num % 10 < 4 ? num % 10 : 0);
    return `${num}${suffixes[pick]}`;
  }, []);

  const getInitials = useCallback((name) => {
    const clean = (name || '').trim();
    if (!clean) return '??';
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, []);

  const formatApptTime12 = useCallback((hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return '';
    const match = hhmm.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return hhmm;
    let h = parseInt(match[1], 10);
    const m = match[2];
    const ampm = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12;
    if (h > 12) h -= 12;
    return `${h}:${m} ${ampm}`;
  }, []);

  const rcStatusMeta = {
    scheduled: { label: 'Scheduled', color: '#5B6B8C', description: 'On the calendar — awaiting' },
    completed: { label: 'Completed', color: '#1F8A70', description: 'Wrapped up' },
    cancelled: { label: 'Cancelled', color: '#B4443C', description: 'Called off' },
  };
  const rcStatusOrder = ['scheduled', 'completed', 'cancelled'];

  const computeBoardState = useCallback((items) => {
    if (!Array.isArray(items) || items.length === 0) {
      return { label: 'Off duty', color: '#A89C85' };
    }
    const pending = items.some((i) => i.status === 'scheduled');
    if (pending) return { label: 'Scheduled', color: '#5B6B8C' };
    if (items.every((i) => i.status === 'completed')) return { label: 'All done', color: '#1F8A70' };
    if (items.every((i) => i.status === 'cancelled')) return { label: 'Stand down', color: '#B4443C' };
    return { label: 'Wrapped up', color: '#756A56' };
  }, []);

  const withAlpha = (hex, alpha) => {
    const h = hex.replace('#', '');
    if (h.length !== 6) return hex;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
  };

  const openRemarksFor = useCallback((itinerary, kind) => {
    if (!itinerary) return;
    setRemarksTargetItId(itinerary.id);
    setRemarksModalKind(kind === 'cancelled' ? 'cancelled' : 'completed');
    setRemarksText((itinerary.itinerary_remarks && String(itinerary.itinerary_remarks)) || '');
    setRemarksSubmitting(false);
    setRemarksModalOpen(true);
  }, []);

  const patchItineraryStatus = useCallback(async (itId, status, remarksRaw) => {
    if (!axiosInstance || !itId) return { ok: false, error: 'Client not ready' };
    try {
      const payload = { status };
      if (typeof remarksRaw === 'string') {
        payload.itinerary_remarks = remarksRaw.slice(0, 1000);
      }
      const res = await axiosInstance.patch(`/routecore/itineraries/${encodeURIComponent(itId)}`, payload);
      return { ok: true, data: res?.data };
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Network error';
      return { ok: false, error: msg };
    }
  }, [axiosInstance]);

  // -------- Log History fetch (runs when modal opens, fired per user click) --------
  useEffect(() => {
    if (!logHistoryOpen) return undefined;
    let cancelled = false;
    (async () => {
      if (!axiosInstance) return;
      setLogHistoryLoading(true);
      setLogHistoryError(null);
      try {
        const res = await axiosInstance.get('/employee_attendance/my-history', { params: { limit: 180, page: 1 } });
        if (cancelled) return;
        const recordsRaw = Array.isArray(res?.data?.records) ? res.data.records : [];
        // Normalize records to Taipei calendar-day buckets
        const byDay = new Map();
        for (const r of recordsRaw) {
          let ts = r.timestamp ? new Date(r.timestamp) : null;
          if (!ts || Number.isNaN(ts.getTime())) continue;
          const y = ts.getFullYear();
          const m = ts.getMonth() + 1;
          const d = ts.getDate();
          const dayKey = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const actionNorm = typeof r.action === 'string' ? r.action.toLowerCase() : '';
          const isIn = actionNorm === 'in' || actionNorm === 'time_in' || actionNorm === 'timein';
          const isOut = actionNorm === 'out' || actionNorm === 'time_out' || actionNorm === 'timeout';
          const entry = {
            ts,
            isIn,
            isOut,
            time12: formatTime12AmPm(ts),
            displayDate: formatDateAsRequested(ts),
            status: (typeof r.status === 'string' && r.status.trim()) || '',
            displayType: (typeof r.type === 'string' && r.type.trim()) || (isIn ? 'Time In' : 'Time Out'),
          };
          const arr = byDay.get(dayKey) || [];
          arr.push(entry);
          byDay.set(dayKey, arr);
        }
        // Produce [{dayKey, displayDate, firstIn?, lastOut?}] sorted descending date
        const daysList = [];
        for (const [dayKey, entries] of byDay.entries()) {
          const sortedAsc = [...entries].sort((a, b) => a.ts.getTime() - b.ts.getTime());
          const firstIn = sortedAsc.find((e) => e.isIn) || null;
          const lastOut = [...sortedAsc].reverse().find((e) => e.isOut) || null;
          const displayDate = sortedAsc[0]?.displayDate || dayKey;
          daysList.push({ dayKey, displayDate, firstIn, lastOut });
        }
        daysList.sort((a, b) => (a.dayKey < b.dayKey ? 1 : a.dayKey > b.dayKey ? -1 : 0));
        setLogHistoryDays(daysList);
      } catch (err) {
        if (cancelled) return;
        console.warn('[LogHistory] fetch failed:', err?.message || err);
        setLogHistoryError(err?.response?.data?.detail || err?.message || 'Could not load log history. Please try again.');
        setLogHistoryDays([]);
      } finally {
        if (!cancelled) setLogHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logHistoryOpen, axiosInstance, logHistoryFired]);

  // -------- Today Itinerary fetch --------
  useEffect(() => {
    if (!itineraryOpen) return undefined;
    let cancelled = false;
    (async () => {
      if (!axiosInstance) return;
      setItineraryLoading(true);
      setItineraryError(null);
      try {
        const res = await axiosInstance.get('/itineraries/my-today');
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setTodayItineraries(list);
      } catch (err) {
        if (cancelled) return;
        console.warn('[Itinerary] fetch failed:', err?.message || err);
        setItineraryError(err?.response?.data?.detail || err?.message || 'Could not load today itinerary. Please try again.');
        setTodayItineraries([]);
      } finally {
        if (!cancelled) setItineraryLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryOpen, axiosInstance, itineraryFired]);

  const hasActivity = activities.length > 0;

  let latestAction = null;
  if (activities.length > 0) {
    const sorted = [...activities].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    latestAction = sorted[sorted.length - 1].type;
  }
  const canTimeIn = !isSubmitting && (latestAction == null || latestAction === 'out');
  const canTimeOut = !isSubmitting && latestAction === 'in';

  const employeeName =
    user?.name ||
    user?.full_name ||
    (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : null) ||
    'Employee User';
  const employeePosition =
    user?.position ||
    user?.job_title ||
    user?.designation ||
    'Team Member';
  const employeeBranch =
    user?.branch ||
    user?.department ||
    user?.office ||
    user?.site ||
    'Main Office';
  const FALLBACK_SCHEDULE = { shiftStart: '08:00', shiftEnd: '17:00' };
  const [workScheduleSchedule, setWorkSchedule] = useState(
    () => {
      if (user?.work_schedule && typeof user.work_schedule === 'object' && user.work_schedule.shiftStart && user.work_schedule.shiftEnd) {
        return user.work_schedule;
      }
      return FALLBACK_SCHEDULE;
    }
  );
  const schedule = workScheduleSchedule || FALLBACK_SCHEDULE;

  useEffect(() => {
    if (!axiosInstance) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/employee_schedule/my-current');
        if (cancelled) return;
        const data = (res && res.data) ? res.data : null;
        if (!data || typeof data !== 'object') return;
        const shiftStart =
          (typeof data.shiftStart === 'string' && data.shiftStart.trim()) ||
          (typeof data.startTime === 'string' && data.startTime.trim()) ||
          ((typeof data.start_time === 'string' && data.start_time.trim()) || '08:00');
        const shiftEnd =
          (typeof data.shiftEnd === 'string' && data.shiftEnd.trim()) ||
          (typeof data.endTime === 'string' && data.endTime.trim()) ||
          ((typeof data.end_time === 'string' && data.end_time.trim()) || '17:00');
        const next = {
          id: data.id ?? data.scheduleId ?? null,
          scheduleId: data.scheduleId ?? data.id ?? null,
          name: typeof data.name === 'string' ? data.name : null,
          displayName: typeof data.displayName === 'string' ? data.displayName : null,
          shiftStart,
          shiftEnd,
          graceMinutes: Number.isFinite(+data.graceMinutes) ? +data.graceMinutes : (Number.isFinite(+data.grace_minutes) ? +data.grace_minutes : 10),
          workDays: Array.isArray(data.workDays) ? data.workDays : (Array.isArray(data.work_days) ? data.work_days : [1,2,3,4,5]),
          lunchMinutes: Number.isFinite(+data.lunchMinutes) ? +data.lunchMinutes : 60,
          totalHours: Number.isFinite(+data.totalHours) ? +data.totalHours : 8,
          source: typeof data.source === 'string' ? data.source : null,
          resolved: Boolean(data.resolved),
          matchedEmployee: Boolean(data.matchedEmployee),
        };
        setWorkSchedule((prev) => {
          try {
            const prevStr = JSON.stringify(prev);
            const nextStr = JSON.stringify(next);
            if (prevStr === nextStr) return prev;
          } catch (_) {}
          return next;
        });
      } catch (err) {
        if (cancelled) return;
        // best-effort fallback already using client default (still shows old static schedule)
        if (!workScheduleSchedule || !workScheduleSchedule.shiftStart) {
          setWorkSchedule(FALLBACK_SCHEDULE);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [axiosInstance, user?.id, user?.email]);

  const pageBg = isLight
    ? 'bg-gradient-to-br from-paper via-porcelain to-paper'
    : 'bg-gradient-to-br from-navy-950 via-navy-900 to-navy-950';

  const pageText = isLight ? 'text-slate-900' : 'text-white';

  const blob1 = isLight ? 'bg-blue-500/5' : 'bg-blue-500/5';
  const blob2 = isLight ? 'bg-amber-500/5' : 'bg-indigo-500/5';

  const cardBg = isLight ? 'bg-white/78 border-slate-200' : 'bg-navy-900/60 border-navy-700/50';

  const logoutBtnBg = isLight
    ? 'bg-red-500/10 border-red-500/30 text-red-600 hover:from-red-500 hover:to-red-600 hover:text-white hover:border-red-500'
    : 'bg-red-500/20 border-red-500/30 text-red-400 hover:from-red-500 hover:to-red-600 hover:text-white hover:border-red-500';

  const nameText = isLight ? 'text-slate-900' : 'text-white';
  const posText = isLight ? 'text-blue-600' : 'text-blue-400';
  const branchIcon = isLight ? 'text-slate-400' : 'text-slate-500';
  const branchText = isLight ? 'text-slate-500' : 'text-slate-400';

  const activeBadge = hasActivity
    ? (isLight ? 'bg-emerald-50 text-emerald-600' : 'bg-emerald-500/15 text-emerald-400')
    : (isLight ? 'bg-red-50 text-red-600' : 'bg-red-500/15 text-red-400');

  const activePing = hasActivity
    ? (isLight ? 'bg-emerald-500' : 'bg-emerald-400')
    : (isLight ? 'bg-red-500' : 'bg-red-400');

  const timeCardBg = isLight ? 'bg-white/78 border-slate-200' : 'bg-navy-900/60 border-navy-700/50';
  const timeInLabel = isLight ? 'text-slate-500' : 'text-slate-500';
  const timeInVal = isLight ? 'text-emerald-600' : 'text-emerald-400';
  const timeOutVal = isLight ? 'text-orange-600' : 'text-orange-400';

  const footerText = isLight ? 'text-slate-400' : 'text-slate-600';

  const modalBg = isLight ? 'bg-white' : 'bg-navy-900';
  const modalBorder = isLight ? 'border-slate-200' : 'border-navy-700/50';
  const modalTitle = isLight ? 'text-slate-900' : 'text-white';
  const modalSub = isLight ? 'text-slate-500' : 'text-slate-400';
  const cancelModalBtn = isLight ? 'bg-slate-100 text-white hover:bg-slate-200' : 'bg-navy-800 text-slate-200 hover:bg-navy-700';
  const logoutModalBtn = 'bg-red-600 text-white hover:bg-red-500';

  return (
    <div className={`new-admin-dash min-h-screen ${pageBg} ${pageText}`}>
      <style>{`
        .intro-stage { perspective: 1400px; perspective-origin: 50% 40%; }
        .intro-item { opacity: 0; transform-style: preserve-3d; will-change: transform, opacity; animation-fill-mode: forwards; animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes intro-flip-up { 0% { opacity: 0; transform: translate3d(0, 60px, -220px) rotateX(-55deg) scale(0.86); filter: blur(6px); } 60% { filter: blur(0); } 100% { opacity: 1; transform: translate3d(0, 0, 0) rotateX(0deg) scale(1); filter: blur(0); } }
        @keyframes intro-zoom-spin { 0% { opacity: 0; transform: translate3d(0, 0, -500px) rotateY(-45deg) rotateZ(-12deg) scale(0.5); filter: blur(10px); } 65% { filter: blur(0); } 100% { opacity: 1; transform: translate3d(0, 0, 0) rotateY(0deg) rotateZ(0deg) scale(1); filter: blur(0); } }
        @keyframes intro-swing-left { 0% { opacity: 0; transform: translate3d(-70px, 0, -200px) rotateY(50deg); filter: blur(5px); } 100% { opacity: 1; transform: translate3d(0, 0, 0) rotateY(0deg); filter: blur(0); } }
        @keyframes intro-swing-right { 0% { opacity: 0; transform: translate3d(70px, 0, -200px) rotateY(-50deg); filter: blur(5px); } 100% { opacity: 1; transform: translate3d(0, 0, 0) rotateY(0deg); filter: blur(0); } }
        @keyframes intro-rise { 0% { opacity: 0; transform: translate3d(0, 24px, -60px); } 100% { opacity: 1; transform: translate3d(0, 0, 0); } }
        .anim-flip-up { animation-name: intro-flip-up; animation-duration: 1s; }
        .anim-zoom-spin { animation-name: intro-zoom-spin; animation-duration: 1.2s; }
        .anim-swing-left { animation-name: intro-swing-left; animation-duration: 0.9s; }
        .anim-swing-right { animation-name: intro-swing-right; animation-duration: 0.9s; }
        .anim-rise { animation-name: intro-rise; animation-duration: 0.7s; }
        @media (prefers-reduced-motion: reduce) { .intro-item { animation: none !important; opacity: 1 !important; transform: none !important; filter: none !important; } }
        .leaflet-container { ${isLight ? 'background: #f1f5f9 !important;' : 'background: #0e2049 !important;'} font-family: inherit !important; }
        .leaflet-control-zoom a { ${isLight ? 'background: #ffffff !important; color: #475569 !important; border-color: #e2e8f0 !important;' : 'background: #0e2049 !important; color: #e2e8f0 !important; border-color: #16306b !important;'} }
        .leaflet-control-zoom a:hover { ${isLight ? 'background: #f1f5f9 !important;' : 'background: #16306b !important;'} }
        .leaflet-control-attribution { ${isLight ? 'background: rgba(248, 250, 252, 0.8) !important; color: #64748b !important;' : 'background: rgba(7, 16, 39, 0.8) !important; color: #64748b !important;'} font-size: 9px !important; }
        .leaflet-popup-content-wrapper { ${isLight ? 'background: #ffffff !important; color: #1e293b !important; border-radius: 12px !important; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12) !important;' : 'background: #0e2049 !important; color: #e2e8f0 !important; border-radius: 12px !important; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3) !important;'} }
        .leaflet-popup-tip { ${isLight ? 'background: #ffffff !important;' : 'background: #0e2049 !important;'} }
      `}</style>

      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 left-1/4 w-96 h-96 ${blob1} rounded-full blur-3xl`} />
        <div className={`absolute bottom-0 right-1/4 w-96 h-96 ${blob2} rounded-full blur-3xl`} />
      </div>

      <div className="intro-stage relative max-w-lg mx-auto px-4 py-6 md:py-8">
        <div
          {...intro('anim-flip-up', 0.05, `backdrop-blur rounded-2xl p-4 mb-6 border ${cardBg}
            ${isLight
              ? 'shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)] border-white/70 ring-1 ring-slate-200/60'
              : 'shadow-[0_10px_40px_-10px_rgba(0,0,0,0.55)] border-white/10 ring-1 ring-white/5'}`)}
        >
          <div className="flex items-center gap-4">
            <button
              onClick={handleLogoutClick}
              title="Log out"
              aria-label="Log out"
              className={`w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br border
                flex items-center justify-center ${logoutBtnBg}
                active:scale-95 transition-all duration-200 group`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" />
              </svg>
            </button>
            <div className="flex-1 min-w-0">
              <h1 className={`text-lg font-bold truncate ${nameText}`}>{employeeName}</h1>
              <p className={`text-sm font-medium ${posText}`}>{employeePosition}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <svg className={`w-3.5 h-3.5 ${branchIcon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className={`text-xs ${branchText}`}>{employeeBranch}</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${activeBadge}`}>
                <span className="relative flex h-2.5 w-2.5">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${activePing}`} />
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${activePing}`} />
                </span>
                {hasActivity ? 'Active' : 'Offline'}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div {...intro('anim-zoom-spin', 0.25)}>
            <AnalogClock />
          </div>
          <div {...intro('anim-rise', 0.6, 'mt-4')}>
            <DigitalClock />
          </div>
        </div>

        <div {...intro('anim-flip-up', 0.75, 'mb-6')}>
          <WorkSchedule
            schedule={schedule}
            timeInRecord={timeInRecord}
            activities={activities}
          />
        </div>

        {(timeInRecord || timeOutRecord) && (
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div {...intro('anim-swing-left', 0.9, `backdrop-blur rounded-xl p-3.5 border ${timeCardBg}
              ${isLight
                ? 'shadow-[0_6px_18px_-10px_rgba(15,23,42,0.2)] border-white/70 ring-1 ring-slate-200/70'
                : 'shadow-[0_6px_24px_-8px_rgba(0,0,0,0.6)] border-white/10 ring-1 ring-white/5'}`)}>
              <p className={`text-xs mb-1 uppercase tracking-wider font-medium ${timeInLabel}`}>Time In</p>
              <p className={`text-base font-mono font-bold ${timeInVal}`}>
                {timeInRecord
                  ? timeInRecord.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })
                  : '--:-- --'}
              </p>
            </div>
            <div {...intro('anim-swing-right', 1, `backdrop-blur rounded-xl p-3.5 border ${timeCardBg}
              ${isLight
                ? 'shadow-[0_6px_18px_-10px_rgba(15,23,42,0.2)] border-white/70 ring-1 ring-slate-200/70'
                : 'shadow-[0_6px_24px_-8px_rgba(0,0,0,0.6)] border-white/10 ring-1 ring-white/5'}`)}>
              <p className={`text-xs mb-1 uppercase tracking-wider font-medium ${timeInLabel}`}>Time Out</p>
              <p className={`text-base font-mono font-bold ${timeOutVal}`}>
                {timeOutRecord
                  ? timeOutRecord.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })
                  : '--:-- --'}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => handleTimeAction('in')}
            disabled={!canTimeIn}
            {...intro('anim-swing-left', 1.1, `relative group px-6 py-4 rounded-2xl font-semibold text-white transition-all duration-300
              bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600
              active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100
              shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40
              ${isLight
                ? 'ring-1 ring-emerald-300/40 border-b-4 border-emerald-900/30 hover:border-b-emerald-800/30'
                : 'ring-1 ring-emerald-300/20 border-b-4 border-black/50 hover:border-black/40'}`)}
          >
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
              </div>
              <span className="text-sm">Time In</span>
            </div>
          </button>

          <button
            onClick={() => handleTimeAction('out')}
            disabled={!canTimeOut}
            {...intro('anim-swing-right', 1.2, `relative group px-6 py-4 rounded-2xl font-semibold text-white transition-all duration-300
              bg-gradient-to-br from-orange-600 to-red-700 hover:from-orange-500 hover:to-red-600
              active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100
              shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40
              ${isLight
                ? 'ring-1 ring-orange-300/40 border-b-4 border-red-900/30 hover:border-b-red-800/30'
                : 'ring-1 ring-orange-300/20 border-b-4 border-black/50 hover:border-black/40'}`)}
          >
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H4a3 3 0 01-3-3V7a3 3 0 013-3h6a3 3 0 013 3v1" />
                </svg>
              </div>
              <span className="text-sm">Time Out</span>
            </div>
          </button>
        </div>

        {/* ----- MORE (3-dots) centered dropdown under Time In / Time Out ----- */}
        <div className="mt-4 flex justify-center w-full">
          <SimpleDropdown
            isOpen={moreMenuOpen}
            onClose={() => setMoreMenuOpen(false)}
            buttonNode={
              <button
                ref={moreButtonRef}
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMoreMenuOpen((v) => !v); }}
                aria-haspopup="menu"
                aria-expanded={moreMenuOpen}
                title="More options"
                style={{ opacity: 1, visibility: 'visible', display: 'inline-flex' }}
                className={`relative group inline-flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-200 active:scale-[0.96]
                  ${isLight
                    ? 'bg-white text-slate-600 hover:text-slate-900 shadow-[0_10px_25px_-12px_rgba(15,23,42,0.18)] border border-slate-200 ring-1 ring-slate-200/60 hover:shadow-[0_14px_30px_-12px_rgba(15,23,42,0.25)] border-b-[3px] border-slate-300/60'
                    : 'bg-navy-850 text-slate-300 hover:text-white shadow-[0_10px_30px_-10px_rgba(0,0,0,0.55)] border border-white/10 ring-1 ring-white/5 hover:shadow-[0_14px_32px_-10px_rgba(0,0,0,0.7)] border-b-[3px] border-black/40'}`}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="5" cy="12" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="19" cy="12" r="2" />
                </svg>
              </button>
            }
            items={[
              {
                label: 'Log History',
                onClick: () => {
                  setLogHistoryError(null);
                  setLogHistoryOpen(true);
                  setLogHistoryFired((v) => v + 1);
                },
                icon: () => (
                  <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
              },
              {
                label: 'Payslip',
                onClick: () => setPayslipOpen(true),
                icon: () => (
                  <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                ),
              },
              {
                label: 'Itinerary',
                onClick: () => {
                  setItineraryError(null);
                  setItineraryOpen(true);
                  setItineraryFired((v) => v + 1);
                },
                icon: () => (
                  <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M9 6.75V15m0 0l-3-3m3 3l3-3M15 6.75V15m0 0l-3-3m3 3l3-3M3 20.25V6.75A2.25 2.25 0 015.25 4.5h13.5A2.25 2.25 0 0121 6.75v13.5a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 20.25z" />
                  </svg>
                ),
              },
            ]}
          />
        </div>

        <div {...intro('anim-flip-up', 1.35, 'mt-6')}>
          <TodaysActivity activities={activities} />
        </div>

        <div {...intro('anim-rise', 1.55, 'mt-8 text-center')}>
          <p className={`text-xs ${footerText}`}>
            iSource Employee Time Tracking System v2.0
          </p>
        </div>
      </div>

      <GreetingModal
        isOpen={modalStep === 'greeting'}
        type={currentAction || 'in'}
        employeeName={employeeName}
        onContinue={handleGreetingContinue}
        onClose={handleCancel}
      />

      <CameraModal
        isOpen={modalStep === 'camera-selfie'}
        mode="selfie"
        onCapture={handleSelfieCaptured}
        onClose={handleCancel}
      />

      <CameraModal
        isOpen={modalStep === 'camera-premises'}
        mode="premises"
        onCapture={handlePremisesCaptured}
        onClose={handleCancel}
      />

      <MapModal
        isOpen={modalStep === 'map'}
        axiosInstance={axiosInstance}
        user={user}
        onConfirm={handleLocationConfirmed}
        onClose={handleCancel}
        saveErrorMsg={saveErrorMsg}
        clearSaveError={() => setSaveErrorMsg('')}
      />

      <RecordingOverlayModal
        isOpen={recordingModalOpen}
        action={recordingModalAction || currentAction || 'in'}
      />

      <SuccessModal
        isOpen={showSuccess}
        type={lastAction}
        timestamp={lastTimestamp}
        onClose={() => setShowSuccess(false)}
      />

      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className={`${modalBg} rounded-2xl overflow-hidden w-full max-w-sm border ${modalBorder} shadow-2xl p-6 text-center`}>
            <div className="w-14 h-14 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" />
              </svg>
            </div>

            <h3 className={`text-lg font-bold mb-2 ${modalTitle}`}>Log out?</h3>
            <p className={`text-sm mb-6 ${modalSub}`}>
              {hasActivity
                ? "You have recorded activity today. Are you sure you want to log out?"
                : "Are you sure you want to log out of your account?"}
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors ${cancelModalBtn}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors ${logoutModalBtn}`}
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -------- LOG HISTORY modal: first time in + last time out per day -------- */}
      <SimpleModal
        isOpen={logHistoryOpen}
        onClose={() => setLogHistoryOpen(false)}
        title="Attendance Log History"
        maxWidthClass="max-w-xl"
      >
        {logHistoryLoading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <svg className="w-8 h-8 text-sky-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <p className="text-sm">Loading log history…</p>
          </div>
        ) : logHistoryError ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75M9.5 15a2.5 2.5 0 105 0M12 20.25a8.25 8.25 0 110-16.5 8.25 8.25 0 010 16.5z" />
              </svg>
            </div>
            <p className="text-sm max-w-xs">{logHistoryError}</p>
            <button
              onClick={() => { setLogHistoryFired((v) => v + 1); setLogHistoryOpen(false); setTimeout(() => setLogHistoryOpen(true), 20); }}
              className="mt-1 px-4 py-2 rounded-xl bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium"
            >
              Retry
            </button>
          </div>
        ) : logHistoryDays.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-2 ${isLight ? 'bg-slate-100 text-slate-400' : 'bg-navy-800 text-slate-500'}`}>
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className={`text-base font-semibold ${isLight ? 'text-slate-700' : 'text-slate-200'}`}>No attendance history yet</p>
            <p className="text-xs">Once you clock days in the system, each day will show here with your first Time In and last Time Out.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {logHistoryDays.map((d) => {
              const inS = d.firstIn?.status || '';
              const outS = d.lastOut?.status || '';
              const badge = (s) => {
                if (!s) return null;
                let cls = 'bg-slate-500/15 text-slate-500 border border-slate-400/20';
                if (s === 'On time') cls = isLight ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/20';
                else if (s.startsWith('Late')) cls = isLight ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-amber-500/15 text-amber-300 border border-amber-400/20';
                else if (s.startsWith('Undertime')) cls = isLight ? 'bg-rose-100 text-rose-700 border border-rose-200' : 'bg-rose-500/15 text-rose-300 border border-rose-400/20';
                return <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-medium whitespace-nowrap ${cls}`}>{s}</span>;
              };
              return (
                <div key={d.dayKey} className={`rounded-2xl p-4 border
                  ${isLight
                    ? 'bg-slate-50/60 border-slate-200'
                    : 'bg-navy-800/50 border-navy-700/50'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className={`text-sm font-semibold ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>{d.displayDate}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`rounded-xl p-3 border ${isLight ? 'bg-white border-emerald-100' : 'bg-navy-900/60 border-emerald-500/15'}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-emerald-500 ${isLight ? 'bg-emerald-50' : 'bg-emerald-500/10'}`}>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14" />
                          </svg>
                        </div>
                        <p className={`text-[11px] uppercase tracking-wider font-semibold ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>First Time In</p>
                      </div>
                      {d.firstIn ? (
                        <div className="flex flex-col gap-1.5">
                          <p className={`text-base font-semibold ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>{d.firstIn.time12 || '--:-- --'}</p>
                          {badge(inS)}
                        </div>
                      ) : (
                        <p className={`text-sm italic ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>No Time In recorded</p>
                      )}
                    </div>
                    <div className={`rounded-xl p-3 border ${isLight ? 'bg-white border-orange-100' : 'bg-navy-900/60 border-orange-500/15'}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-orange-500 ${isLight ? 'bg-orange-50' : 'bg-orange-500/10'}`}>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7" />
                          </svg>
                        </div>
                        <p className={`text-[11px] uppercase tracking-wider font-semibold ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>Last Time Out</p>
                      </div>
                      {d.lastOut ? (
                        <div className="flex flex-col gap-1.5">
                          <p className={`text-base font-semibold ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>{d.lastOut.time12 || '--:-- --'}</p>
                          {badge(outS)}
                        </div>
                      ) : (
                        <p className={`text-sm italic ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>No Time Out recorded</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SimpleModal>

      {/* -------- PAYSLIP modal: Coming Soon -------- */}
      <SimpleModal
        isOpen={payslipOpen}
        onClose={() => setPayslipOpen(false)}
        title="Payslip"
        maxWidthClass="max-w-md"
      >
        <div className="flex flex-col items-center justify-center py-10 text-center gap-4">
          <div className={`relative w-20 h-20 rounded-3xl flex items-center justify-center
            ${isLight ? 'bg-gradient-to-br from-amber-100 to-amber-200' : 'bg-gradient-to-br from-amber-500/20 to-yellow-400/10'}`}>
            <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="absolute -top-1 -right-1 flex items-center justify-center w-7 h-7 rounded-full bg-white/90 text-amber-500 shadow-lg ring-1 ring-amber-200">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
            </span>
          </div>
          <h3 className={`text-xl font-bold ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>Coming Soon</h3>
          <p className={`text-sm max-w-xs ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
            Payslip portal for employee download and viewing is coming soon. You will be able to view and download your payslips directly from this modal.
          </p>
        </div>
      </SimpleModal>

      {/* -------- TODAY ITINERARY modal -------- */}
      <SimpleModal
        isOpen={itineraryOpen}
        onClose={() => setItineraryOpen(false)}
        title="Today's Itinerary"
        maxWidthClass="max-w-xl"
      >
        {itineraryLoading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <svg className="w-8 h-8 text-emerald-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <p className="text-sm">Loading today's itinerary…</p>
          </div>
        ) : itineraryError ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75M9.5 15a2.5 2.5 0 105 0M12 20.25a8.25 8.25 0 110-16.5 8.25 8.25 0 010 16.5z" />
              </svg>
            </div>
            <p className="text-sm max-w-xs">{itineraryError}</p>
            <button
              onClick={() => { setItineraryFired((v) => v + 1); setItineraryOpen(false); setTimeout(() => setItineraryOpen(true), 20); }}
              className="mt-1 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium"
            >
              Retry
            </button>
          </div>
        ) : todayItineraries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-2 ${isLight ? 'bg-emerald-50 text-emerald-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M9 6.75V15m0 0l-3-3m3 3l3-3M15 6.75V15m0 0l-3-3m3 3l3-3M3 20.25V6.75A2.25 2.25 0 015.25 4.5h13.5A2.25 2.25 0 0121 6.75v13.5a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 20.25z" />
              </svg>
            </div>
            <p className={`text-base font-semibold ${isLight ? 'text-slate-700' : 'text-slate-200'}`}>No itinerary scheduled for today</p>
            <p className="text-xs max-w-xs">Your today itinerary for field calls or visits will appear here once assigned to you.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {(() => {
              const first = todayItineraries[0] || {};
              const employeeName = (typeof first.employeeName === 'string' && first.employeeName.trim()) || (typeof first.employee_name === 'string' && first.employee_name.trim()) || 'Assigned Employee';
              const employeeRole = (typeof first.employeeRole === 'string' && first.employeeRole.trim()) || (typeof first.employee_role === 'string' && first.employee_role.trim()) || '';
              const employeeColor = (typeof first.employeeColor === 'string' && first.employeeColor.trim()) || '#5B6B8C';
              const board = computeBoardState(todayItineraries);
              return (
                <div className={`flex items-center gap-3 rounded-2xl border p-3 ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-navy-900/40 border-navy-700/60'}`}>
                  <div className="w-11 h-11 rounded-full shrink-0 flex items-center justify-center text-white font-bold text-sm shadow-inner"
                    style={{ backgroundColor: employeeColor }}>
                    {getInitials(employeeName)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-bold truncate ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>{employeeName}</p>
                    {employeeRole && (
                      <p className={`text-xs mt-0.5 truncate ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>{employeeRole}</p>
                    )}
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider border"
                    style={{ color: board.color, backgroundColor: withAlpha(board.color, 0.12), borderColor: withAlpha(board.color, 0.3) }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: board.color }} />
                    {board.label}
                  </span>
                </div>
              );
            })()}

            <div className="flex flex-col gap-3">
              {todayItineraries.map((rec, idx) => {
                const itId = rec.id ?? rec._id ?? `${rec.date}-${rec.seq ?? idx}-${rec.title || ''}`;
                const seq = typeof rec.seq === 'number' ? rec.seq : (idx + 1);
                const status = (typeof rec.status === 'string' && rcStatusOrder.includes(rec.status)) ? rec.status : 'scheduled';
                const title = (typeof rec.title === 'string' && rec.title.trim()) || 'Unnamed Stop';
                const notes = (typeof rec.notes === 'string' && rec.notes.trim()) || '';
                const locationName = (typeof rec.locationName === 'string' && rec.locationName.trim()) || (typeof rec.address === 'string' && rec.address.trim()) || '';
                const apptTime = (typeof rec.appointmentTime === 'string' && rec.appointmentTime.trim()) || null;
                const employeeColor = (typeof rec.employeeColor === 'string' && rec.employeeColor.trim()) || '#5B6B8C';
                const cancelled = status === 'cancelled';
                const meta = rcStatusMeta[status];
                return (
                  <div key={itId}
                    className={`rounded-2xl p-4 border
                      ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-navy-850/60 border-navy-700/60'}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center font-black text-[13px] tracking-tight"
                          style={{ backgroundColor: withAlpha(meta.color, 0.12), color: meta.color, border: `1px solid ${withAlpha(meta.color, 0.3)}` }}>
                          {ordinalUp(seq)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            {apptTime && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border"
                                style={{ backgroundColor: withAlpha(employeeColor, 0.14), color: employeeColor, borderColor: withAlpha(employeeColor, 0.35) }}>
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 10.414l3.293 3.293 1.414-1.414L14 11V5h-2v7.414z" />
                                </svg>
                                {formatApptTime12(apptTime)}
                              </span>
                            )}
                          </div>
                          <p className={`text-[15px] font-bold leading-snug ${cancelled ? 'line-through opacity-60' : ''} ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>
                            {title}
                          </p>
                          {locationName && (
                            <div className="flex items-start gap-1.5 mt-1.5">
                              <svg className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2a7 7 0 017 7c0 4.9-6.1 10.6-6.6 11.1-.3.3-.8.3-1.1 0C8.1 19.6 2 13.9 2 9a7 7 0 017-7h3zm0 6a3 3 0 100 6 3 3 0 000-6z" />
                              </svg>
                              <p className={`text-xs break-words ${isLight ? 'text-amber-700' : 'text-amber-300'}`}>{locationName}</p>
                            </div>
                          )}
                          {notes && (
                            <p className={`mt-2 text-xs leading-relaxed whitespace-pre-wrap break-words ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>
                              {notes}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t"
                      style={{ borderColor: isLight ? '#e2e8f0' : 'rgba(148,163,184,0.15)' }}>
                      {rcStatusOrder.map((st) => {
                        const sm = rcStatusMeta[st];
                        const active = status === st;
                        const isScheduled = st === 'scheduled';
                        const handleClick = () => {
                          if (remarksSubmitting) return;
                          if (isScheduled) {
                            patchItineraryStatus(itId, 'scheduled', '').then((res) => {
                              if (res.ok) {
                                setItineraryFired((v) => v + 1);
                                setItineraryOpen(false);
                                setTimeout(() => setItineraryOpen(true), 20);
                              } else {
                                setItineraryError(res.error || 'Could not update status. Please try again.');
                              }
                            });
                          } else {
                            openRemarksFor(rec, st);
                          }
                        };
                        return (
                          <button
                            key={st}
                            type="button"
                            onClick={handleClick}
                            disabled={remarksSubmitting}
                            className="px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border transition-all duration-150 disabled:opacity-50"
                            style={{
                              color: sm.color,
                              backgroundColor: active ? withAlpha(sm.color, 0.12) : 'transparent',
                              borderColor: active ? sm.color : (isLight ? '#cbd5e1' : 'rgba(148,163,184,0.25)'),
                              boxShadow: active ? `inset 0 0 0 1px ${withAlpha(sm.color, 0.4)}` : 'none'
                            }}
                          >
                            {sm.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </SimpleModal>

      {/* -------- ITINERARY REMARKS parametric modal -------- */}
      {remarksModalOpen && (
        <SimpleModal
          isOpen={remarksModalOpen}
          onClose={() => { if (!remarksSubmitting) setRemarksModalOpen(false); }}
          title=""
          maxWidthClass="max-w-lg"
        >
          {(() => {
            const isCompleted = remarksModalKind === 'completed';
            const accentHex = isCompleted ? '#1F8A70' : '#B4443C';
            const placeholder = isCompleted
              ? 'Add completion remarks (optional) — what was accomplished, deliverables handed over, next steps…'
              : 'Add cancellation reason (optional) — why this stop cannot be completed today…';
            return (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold tracking-tight" style={{ color: accentHex }}>
                    Itinerary Remarks
                  </h2>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider border"
                    style={{ color: accentHex, backgroundColor: withAlpha(accentHex, 0.1), borderColor: withAlpha(accentHex, 0.3) }}>
                    {isCompleted ? 'Completed' : 'Cancelled'}
                  </span>
                </div>

                <div className={`rounded-2xl border-2 p-3 ${isLight ? '' : ''}`}
                  style={{ borderColor: accentHex, backgroundColor: isLight ? withAlpha(accentHex, 0.04) : withAlpha(accentHex, 0.08) }}>
                  <textarea
                    rows={6}
                    maxLength={1000}
                    value={remarksText}
                    placeholder={placeholder}
                    disabled={remarksSubmitting}
                    onChange={(e) => setRemarksText(e.target.value.slice(0, 1000))}
                    className={`w-full resize-none outline-none bg-transparent text-sm leading-relaxed disabled:opacity-60 ${isLight ? 'text-slate-800 placeholder-slate-400' : 'text-slate-100 placeholder-slate-500'}`}
                  />
                  <div className="flex items-center justify-between mt-2 pt-2 border-t" style={{ borderColor: withAlpha(accentHex, 0.18) }}>
                    <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: withAlpha(accentHex, 0.7) }}>
                      {remarksSubmitting ? 'Submitting…' : 'Max 1000 characters'}
                    </span>
                    <span className="text-xs font-mono font-semibold tabular-nums" style={{ color: accentHex }}>
                      {remarksText.length} / 1000
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    disabled={remarksSubmitting}
                    onClick={() => { if (!remarksSubmitting) setRemarksModalOpen(false); }}
                    className="px-4 py-2 rounded-xl text-sm font-semibold border transition-all duration-150 disabled:opacity-50 hover:bg-rose-500/10"
                    style={{ color: '#B4443C', borderColor: 'rgba(180,68,60,0.3)' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={remarksSubmitting}
                    onClick={async () => {
                      if (remarksSubmitting) return;
                      setRemarksSubmitting(true);
                      const target = remarksTargetItId;
                      const kind = remarksModalKind;
                      const trimmed = (remarksText || '').slice(0, 1000).trim();
                      const res = await patchItineraryStatus(target, kind, trimmed);
                      setRemarksSubmitting(false);
                      if (res.ok) {
                        setRemarksModalOpen(false);
                        setRemarksText('');
                        setRemarksTargetItId(null);
                        setItineraryFired((v) => v + 1);
                        setItineraryOpen(false);
                        setTimeout(() => setItineraryOpen(true), 20);
                      } else {
                        setItineraryError(res.error || 'Could not update itinerary. Please try again.');
                      }
                    }}
                    className="px-4 py-2 rounded-xl text-sm font-semibold border transition-all duration-150 disabled:opacity-50 hover:bg-emerald-500/10 inline-flex items-center gap-2"
                    style={{ color: '#1F8A70', borderColor: 'rgba(31,138,112,0.3)' }}
                  >
                    {remarksSubmitting && (
                      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                        <path strokeLinecap="round" d="M21 12a9 9 0 11-6.219-8.56" />
                      </svg>
                    )}
                    Confirm
                  </button>
                </div>
              </div>
            );
          })()}
        </SimpleModal>
      )}
    </div>
  );
}
